import axios from 'axios';
import { tokenStore } from './tokenStore';
import { getUmsatzToken } from './umsatz';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = tokenStore.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Umsatz-Sperre (30.10.2026): Freigabe-Token des Inhabers; der Server prüft ihn, ohne ihn kommen keine Umsätze
  const umsatz = getUmsatzToken();
  if (umsatz) config.headers['X-Umsatz-Token'] = umsatz;
  return config;
});

// Handle 401 responses — clear tokens and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Öffentliche, login-freie Seiten (QR-Buchung, Capture-Formular) nie auf
      // /login umleiten — dort feuern globale Provider (z. B. Settings) 401s,
      // die für Besucher ohne Konto völlig normal sind.
      const p = window.location.pathname;
      const isPublicPage = p.startsWith('/buchung') || p.startsWith('/capture')
        || p.startsWith('/zinzino') || p.startsWith('/ptl')
        // Probetrainings-Funnel (+ Alt-Slugs der gedruckten QR-Codes) sind
        // login-frei — /funnel deckt auch /funnelkids ab
        || p.startsWith('/funnel') || p.startsWith('/frauen')
        || p.startsWith('/kids-funnel')
        // Kurs-Landingpages: /kurs/:slug ist die oeffentliche Werbeseite
        || p.startsWith('/kurs/')
        // Trainer-Kalender per Link (ohne Login)
        || p.startsWith('/trainer/')
        // Anwesenheits-Tafel fuers Saeulen-iPad (06.10.2026), Token statt Login
        || p.startsWith('/tafel/')
        // Check-Up am Studio-Tablet (28.10.2026), Token statt Login
        || p.startsWith('/checkup/')
        || p.startsWith('/kampfsportfunnel') || p.startsWith('/probetrainings-buchung')
        // Sommerfest-Festkarte: /fest und /fest/k/:code sind Gastseiten ohne
        // Login (QR-Ziel am Eingang). /fest/scan und /fest/dashboard SIND
        // geschützt — deren eigener Router-Guard (ProtectedMinimalRoute)
        // leitet bei fehlendem Login weiterhin korrekt auf /login um; hier
        // geht es nur darum, den harten window.location-Redirect bei einem
        // Hintergrund-401 (z. B. globaler Settings-Provider) zu unterdrücken.
        || p.startsWith('/fest')
        // Rechtstexte und App-Hilfe. Sie MÜSSEN ohne Anmeldung lesbar sein:
        // /datenschutz und /app-hilfe sind die Adressen, die im App Store
        // hinterlegt sind — Apple ruft sie bei jeder Prüfung abgemeldet auf.
        // Unabhängig davon verlangt Art. 12 DSGVO, dass die Datenschutz-
        // erklärung leicht zugänglich ist; hinter einem Mitarbeiter-Login ist
        // sie das nicht. Ohne diese Ausnahme warf ein Hintergrund-401 des
        // Settings-Providers jeden abgemeldeten Besucher auf /login.
        || p.startsWith('/datenschutz') || p.startsWith('/impressum')
        || p.startsWith('/agb') || p.startsWith('/cookies')
        || p.startsWith('/app-hilfe') || p.startsWith('/app-nutzungsbedingungen')
        // Hilfe-Formular fuer Mitglieder (30.09.2026): Ziel des gedruckten QR-Codes
        // auf dem Aushang und des Links in der Rundmail — Adresse bleibt /hilfe
        || p === '/hilfe' || p.startsWith('/hilfe/')
        // Unterschrift-Station (03.10.2026): /u/<token> ist die QR-Seite des Kunden (ohne Anmeldung);
        // /unterschrift und /trainer sind Stationsseiten, die bei abgelaufener Sitzung selbst
        // „Bitte neu anmelden" zeigen statt hart auf /login zu springen.
        || p.startsWith('/u/') || p === '/unterschrift' || p === '/trainer'
        // Trainer-Webseite (10.10.2026): eigene Anmeldung per E-Mail-Code, kein CRM-Konto
        || p === '/coach' || p.startsWith('/coach/')
        // Gastpass-Kaufseite (11.10.2026)
        || p === '/gastpass'
        // P1 Coaching: Danke-Seite nach Stripe (12.10.2026)
        || p === '/coaching-bezahlt' || p === '/p1-coaching';
      if (!isPublicPage && !url.includes('/auth/me') && !url.includes('/auth/login') && !url.includes('/auth/register')) {
        tokenStore.clear();
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  getConfig: () => api.get('/auth/config'),
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  googleSession: (session_id) => api.post('/auth/google/session', { session_id }),
  me: () => api.get('/auth/me'),
  myPermissions: () => api.get('/auth/my-permissions'),
  verlaengern: () => api.post('/auth/verlaengern'),   // gleitende Sitzung, wirkt nur fuer Stations-Rollen
  logout: () => api.post('/auth/logout'),
  users: () => api.get('/auth/users'),
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FA: (code) => api.post('/auth/2fa/verify', { code }),
  disable2FA: () => api.post('/auth/2fa/disable'),
};

// Leads
export const leadsAPI = {
  // Familie anlegen (26.10.2026): Elternteil + mehrere Kinder mit derselben Nummer, je Kind ein Lead
  familie: (daten) => api.post('/familie/anlegen', daten),
  // Kanal-Zeitstrahl (v5, M3)
  zeitstrahl: (id, params) => api.get(`/leads/${id}/zeitstrahl`, { params }),
  zeitstrahlAntwort: (id, konversation_id, text) => api.post(`/leads/${id}/zeitstrahl/antwort`, { konversation_id, text }),
  list: (params) => api.get('/leads', { params }),
  getAll: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  // R2: Statuswechsel kann naechster_schritt {was, faellig_am} oder grund
  // (verloren) mitgeben — sonst lehnt das Backend defekte Wechsel ab.
  updateStage: (id, stage, extra = {}) => api.patch(`/leads/${id}/stage`, { stage, ...extra }),
  assign: (id, assigned_to) => api.patch(`/leads/${id}/assign`, { assigned_to }),
  bulkAssign: (lead_ids, assigned_to) => api.post('/leads/bulk-assign', { lead_ids, assigned_to }),
  // Archiv: raus aus den Arbeitslisten, nichts wird geloescht
  archiv: (id, archiviert, grund = '') => api.patch(`/leads/${id}/archiv`, { archiviert, grund }),
  // App-Rolle: '' entzieht, 'trainer'/'admin' schaltet den Trainer-Bereich frei
  appRolle: (id, app_role) => api.patch(`/leads/${id}/app-rolle`, { app_role }),
  headcoach: (id, aktiv) => api.patch(`/trainingsplaner/headcoach/${id}`, { aktiv }),   // Trainingsplaner A0: darf in der App jeden Zyklus bearbeiten
  archivAlte: (data) => api.post('/leads/archiv/alte', data),
  importCSV: (formData) => api.post('/leads/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  previewCSV: (formData) => api.post('/leads/import-csv/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportCSV: (params) => api.get('/leads/export-csv', { params, responseType: 'blob' }),
  probetrainings: (params) => api.get('/leads/probetrainings', { params }),
};

// Projekte & Events im Vertrieb: Bedarf -> Checkliste -> Deadlines
export const vorhabenAPI = {
  liste: (params) => api.get('/vorhaben', { params }),
  meineAufgaben: (params) => api.get('/vorhaben/meine-aufgaben', { params }),
  anlegen: (data) => api.post('/vorhaben', data),
  detail: (id) => api.get(`/vorhaben/${id}`),
  aendern: (id, data) => api.put(`/vorhaben/${id}`, data),
  loeschen: (id) => api.delete(`/vorhaben/${id}`),
  bedarfAnlegen: (id, data) => api.post(`/vorhaben/${id}/bedarf`, data),
  bedarfAendern: (id, bid, data) => api.put(`/vorhaben/${id}/bedarf/${bid}`, data),
  bedarfLoeschen: (id, bid) => api.delete(`/vorhaben/${id}/bedarf/${bid}`),
  bedarfVorlage: (id) => api.post(`/vorhaben/${id}/bedarf/vorlage`),
  // Der Schritt, um den es geht: aus Bedarf wird ein Punkt mit Deadline
  bedarfEinplanen: (id, bid, data) => api.post(`/vorhaben/${id}/bedarf/${bid}/aufgabe`, data),
  aufgabeAnlegen: (id, data) => api.post(`/vorhaben/${id}/aufgaben`, data),
  aufgabeAendern: (id, aid, data) => api.patch(`/vorhaben/${id}/aufgaben/${aid}`, data),
  aufgabeLoeschen: (id, aid) => api.delete(`/vorhaben/${id}/aufgaben/${aid}`),
  checklisteCSV: (id) => api.get(`/vorhaben/${id}/checkliste.csv`, { responseType: 'blob' }),
};

// Properties
// Activities
export const activitiesAPI = {
  list: (params) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
  delete: (id) => api.delete(`/activities/${id}`),
};

// Follow-ups
export const followUpsAPI = {
  list: (params) => api.get('/follow-ups', { params }),
  create: (data) => api.post('/follow-ups', data),
  update: (id, data) => api.put(`/follow-ups/${id}`, data),
  delete: (id) => api.delete(`/follow-ups/${id}`),
};

// Dashboard
export const dashboardAPI = {
  get: (params) => api.get('/dashboard', { params }),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Google-Bewertung für öffentliche Seiten (/probetraining), pflegt nur die Leitung (18.10.2026)
export const googleBewertungAPI = {
  get: () => api.get('/einstellungen/google-bewertung'),
  setzen: (data) => api.put('/einstellungen/google-bewertung', data),
  entfernen: () => api.delete('/einstellungen/google-bewertung'),
};

// v8 M20: Bewertungs-Automation (backend/bewertung/)
export const bewertungAPI = {
  einstellungen: () => api.get('/bewertung/einstellungen'),
  setzen: (data) => api.put('/bewertung/einstellungen', data),
  stats: (tage = 90) => api.get('/bewertung/stats', { params: { tage } }),
  turniererfolge: (leadId) => api.get(`/bewertung/turniererfolg/${leadId}`),
  turniererfolgEintragen: (data) => api.post('/bewertung/turniererfolg', data),
};

// Datenzugang nur zum Lesen für die Claude-Sitzung über die API (19.10.2026, backend/datenzugang.py)
export const datenzugangAPI = {
  bereiche: () => api.get('/datenzugang/bereiche'),
  liste: () => api.get('/datenzugang'),
  anlegen: (data) => api.post('/datenzugang', data),
  widerrufen: (id) => api.post(`/datenzugang/${id}/widerrufen`),
  protokoll: (id) => api.get(`/datenzugang/${id}/protokoll`, { params: { limit: 50 } }),
};

// AI
export const aiAPI = {
  scoreLead: (leadId) => api.post(`/ai/score-lead/${leadId}`),
  generateDescription: (propertyId) => api.post(`/ai/generate-description/${propertyId}`),
  models: () => api.get('/ai/models'),
};

// Capture (public)
export const captureAPI = {
  getConfig: () => axios.get(`${API}/capture/form-config`),
  submit: (data) => axios.post(`${API}/capture/submit`, data),
};

// Beratung (öffentliche Buchung — raw axios: der shared Client würde
// bei 401 Tokens löschen und auf /login redirecten)
export const beratungAPI = {
  getConfig: () => axios.get(`${API}/beratung/public/config`),
  getSlots: () => axios.get(`${API}/beratung/public/slots`),
  buchen: (data) => axios.post(`${API}/beratung/public/buchen`, data),
  absagen: (token) => axios.post(`${API}/beratung/public/absage/${token}`),
  icsUrl: (token) => `${API}/beratung/public/ics/${token}`,
};

// Reaktivierung (Abwesenheits-Erkennung, Rückruf-Aufgaben, PT-Termine)
// Lead-Anrufliste (24.09.2026): Kampagnen, Arbeitsliste, Gespraechsmaske
export const anrufkampagnenAPI = {
  liste: () => api.get('/kampagnen/anrufe'),
  werte: () => api.get('/kampagnen/anrufe/werte'),
  skript: () => api.get('/kampagnen/anrufe/skript'),
  vorschau: (filter) => api.post('/kampagnen/anrufe/vorschau', filter),
  anlegen: (daten) => api.post('/kampagnen/anrufe', daten),
  // von Hand (07.10.2026): Leads suchen und anhaken, von Hand eintragen, in laufende Kampagne legen
  leadsSuchen: (q, nur = 'alle') => api.get('/kampagnen/anrufe/leads', { params: { q, nur } }),
  abgleichListe: (zeilen) => api.post('/kampagnen/anrufe/abgleich', { zeilen }),
  hinzufuegen: (id, daten) => api.post(`/kampagnen/anrufe/${id}/eintraege`, daten),
  entfernen: (id, eintragId) => api.delete(`/kampagnen/anrufe/${id}/eintraege/${eintragId}`),
  notiz: (id, eintragId, text) => api.put(`/kampagnen/anrufe/${id}/eintraege/${eintragId}/notiz`, { text }),
  uebersicht: (id) => api.get(`/kampagnen/anrufe/${id}`),
  naechster: (id, eintragId) => api.get(`/kampagnen/anrufe/${id}/naechster`, { params: eintragId ? { eintrag_id: eintragId } : {} }),
  ergebnis: (id, eintragId, daten) => api.post(`/kampagnen/anrufe/${id}/eintraege/${eintragId}/ergebnis`, daten),
  beenden: (id) => api.post(`/kampagnen/anrufe/${id}/beenden`),
  oeffnen: (id) => api.post(`/kampagnen/anrufe/${id}/oeffnen`),
};

// Vorauszahler aus dem Bestand (25.09.2026)
export const vorauszahlerAPI = {
  liste: () => api.get('/vorauszahler'),
  ergebnis: (membershipId, daten) => api.post(`/vorauszahler/${membershipId}/ergebnis`, daten),
};

export const reaktivierungAPI = {
  uebersicht: () => api.get('/reaktivierung/uebersicht'),
  arbeitsliste: () => api.get('/reaktivierung/arbeitsliste'),
  abwesenheit: () => api.get('/reaktivierung/abwesenheit'),
  kuendigungsrisiko: () => api.get('/reaktivierung/kuendigungsrisiko'),
  kontext: (taskId) => api.get(`/reaktivierung/tasks/${taskId}/kontext`),
  start: (taskId) => api.post(`/reaktivierung/tasks/${taskId}/start`),
  ergebnis: (taskId, data) => api.post(`/reaktivierung/tasks/${taskId}/ergebnis`, data),
  slots: () => api.get('/reaktivierung/slots'),
  termin: (taskId, data) => api.post(`/reaktivierung/tasks/${taskId}/termin`, data),
  auswertung: () => api.get('/reaktivierung/auswertung'),
  scan: () => api.post('/reaktivierung/scan'),
};

// Mandanten (Zinzino / PTL) — öffentliche Landingpage-Endpunkte + CRM-Verwaltung
export const mandantenAPI = {
  // öffentlich (kein Login)
  publicConfig: (mandant) => api.get(`/public/${mandant}/config`),
  publicSlots: (mandant) => api.get(`/public/${mandant}/slots`),
  publicBuchen: (mandant, data) => api.post(`/public/${mandant}/termin`, data),
  icsUrl: (mandant, id) => `${API}/public/${mandant}/termin/${id}.ics`,
  // geschützt (CRM-Login)
  liste: () => api.get('/mandanten'),
  termine: (params) => api.get('/termine', { params }),
  terminUpdate: (id, data) => api.patch(`/termine/${id}`, data),
  terminErgebnis: (id, data) => api.post(`/termine/${id}/ergebnis`, data),
  terminLoeschen: (id) => api.delete(`/termine/${id}`),
  termineCSV: (mandant) => api.get('/termine/export.csv', { params: { mandant }, responseType: 'blob' }),
  leads: (params) => api.get('/mandanten-leads', { params }),
  leadUpdate: (id, data) => api.patch(`/mandanten-leads/${id}`, data),
  leadLoeschen: (id) => api.delete(`/mandanten-leads/${id}`),
  leadsCSV: (mandant) => api.get('/mandanten-leads/export.csv', { params: { mandant }, responseType: 'blob' }),
  sperren: (mandant) => api.get('/sperren', { params: { mandant } }),
  sperreAnlegen: (mandant, datum) => api.post('/sperren', { mandant, datum }),
  sperreLoeschen: (mandant, datum) => api.delete('/sperren', { params: { mandant, datum } }),
};

export const emailKanalAPI = {
  domainStatus: () => api.get('/email/domain-status'),
  vorlagen: (mandant) => api.get('/email/vorlagen', { params: mandant ? { mandant } : {} }),
  vorlageSchreiben: (data) => api.put('/email/vorlagen', data),
  nachrichten: (an) => api.get('/email/nachrichten', { params: an ? { an } : {} }),
  senden: (data) => api.post('/email/senden', data),
  leadNachrichten: (leadId) => api.get(`/email/lead/${leadId}`),
};

export const probetrainingAPI = {
  // Setzt den Status im Termin-Kern UND am Lead — beides in einem Aufruf,
  // sonst laufen die zwei Wahrheiten auseinander.
  status: (buchungId, status) =>
    api.post(`/probetrainings/${buchungId}/status`, null, { params: { status } }),
  // Sales Tracking (24.09.2026): Ergebnis nach dem Probetraining
  // Lead ohne Buchung oder von der Lead-Seite aus: dieselbe Funktion wie die Liste (30.10.2026)
  leadStatus: (leadId, status) => api.post(`/probetrainings/lead/${leadId}/status`, null, { params: { status } }),
  ergebnis: (buchungId, daten) => api.post(`/probetrainings/${buchungId}/ergebnis`, daten),
  leadErgebnis: (leadId, daten) => api.post(`/probetrainings/lead/${leadId}/ergebnis`, daten),
  // Tag-1-Angebot von Hand (02.11.2026), auch nach verpasstem Termin: gilt ab heute 7 Tage, Aufgabe sofort
  tag1Manuell: (leadId, grund) => api.post(`/probetrainings/lead/${leadId}/tag1`, { grund }),
  ergebnisWerte: () => api.get('/probetrainings/ergebnis-werte'),
  // Termin verschieben (06.10.2026): bisheriger Termin + Kurse, freie Termine, Umbuchung über den Termin-Kern
  verschiebenInfo: (buchungId) => api.get(`/probetrainings/${buchungId}/verschieben`),
  verschiebenSlots: (buchungId, params) => api.get(`/probetrainings/${buchungId}/verschieben/slots`, { params }),
  verschieben: (buchungId, daten) => api.post(`/probetrainings/${buchungId}/verschieben`, daten),
};

// Sales Tracking (24.09.2026): Abschlussquote je Zeitraum, Berater, Herkunft
export const salesTrackingAPI = {
  kennzahlen: (params) => api.get('/kennzahlen/vertrieb', { params }),
  exportCsv: (params) => api.get('/kennzahlen/vertrieb/export.csv', { params, responseType: 'blob' }),
};

export const seitenaufrufeAPI = {
  uebersicht: (seite, tage) => api.get('/tracking/uebersicht', { params: { seite, tage } }),
  exportCSV: (seite, tage) => api.get('/tracking/export.csv', { params: { seite, tage }, responseType: 'blob' }),
  // bestaetigung=<seite> ist Pflicht — verhindert ein versehentliches Leeren
  // per Direktaufruf der URL.
  zuruecksetzen: (seite) => api.post('/tracking/zuruecksetzen', null,
    { params: { seite, bestaetigung: seite } }),
};

export const reportingAPI = {
  kennzahlen: (params) => api.get('/reporting/kennzahlen', { params }),
  trichterKanal: (params) => api.get('/reporting/trichter-kanal', { params }),
  verlauf: (mandant) => api.get('/reporting/verlauf', { params: { mandant } }),
  exportCSV: (params) => api.get('/reporting/export.csv', { params, responseType: 'blob' }),
  werbekosten: (mandant) => api.get('/reporting/werbekosten', { params: { mandant } }),
  werbekostenAnlegen: (data) => api.post('/reporting/werbekosten', data),
  werbekostenLoeschen: (id) => api.delete(`/reporting/werbekosten/${id}`),
  // Kampagnen-Trichter (26.09.2026)
  kampagne: (params) => api.get('/reporting/kampagne', { params }),
  kampagneListe: () => api.get('/reporting/kampagne/liste'),
  bioKlicks: (kampagne) => api.get('/reporting/kampagne/bio-klicks', { params: { kampagne } }),
  bioKlicksSetzen: (data) => api.post('/reporting/kampagne/bio-klicks', data),
  bioKlicksLoeschen: (id) => api.delete(`/reporting/kampagne/bio-klicks/${id}`),
};

export const zinzinoCockpitAPI = {
  laden: (wochen) => api.get('/zinzino-cockpit', { params: { wochen } }),
  wocheSchreiben: (data) => api.put('/zinzino-cockpit/woche', data),
  wocheLoeschen: (person, wocheStart) =>
    api.delete('/zinzino-cockpit/woche', { params: { person, woche_start: wocheStart } }),
};

// Superchat — WhatsApp/Instagram-Posteingang + KI-Agent
export const superchatAPI = {
  // Pop-up „Kunde hat geschrieben“ (26.10.2026): neue Kundennachrichten seit dem Zeiger
  neu: (seit) => api.get('/superchat/neu', { params: seit ? { seit } : {} }),
  postfachAbrufen: () => api.post('/email/posteingang/abrufen'),
  konversationen: (params) => api.get('/superchat/konversationen', { params }),
  konversationenZuordnen: () => api.post('/superchat/konversationen/zuordnen'),
  konversation: (id) => api.get(`/superchat/konversationen/${id}`),
  konversationUpdate: (id, data) => api.patch(`/superchat/konversationen/${id}`, data),
  senden: (id, text) => api.post(`/superchat/konversationen/${id}/senden`, { text }),
  anhangSenden: (id, datei, text) => { const fd = new FormData(); fd.append('datei', datei, datei.name); if (text) fd.append('text', text);
    return api.post(`/superchat/konversationen/${id}/anhang`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }); },
  konversationLoeschen: (id) => api.delete(`/superchat/konversationen/${id}`),
  konversationFuerLead: (leadId, kanal) => api.post(`/superchat/konversationen/fuer-lead/${leadId}`, null, { params: kanal ? { kanal } : {} }),
  templateSenden: (id, data) => api.post(`/superchat/konversationen/${id}/template`, data),
  vorschlagFreigeben: (laufId, textOverride) =>
    api.post(`/superchat/laeufe/${laufId}/freigeben`, { text_override: textOverride || null }),
  vorschlagVerwerfen: (laufId) => api.post(`/superchat/laeufe/${laufId}/verwerfen`),
  wissensbasis: (params) => api.get('/superchat/wissensbasis', { params }),
  wissensbasisAnlegen: (data) => api.post('/superchat/wissensbasis', data),
  wissensbasisAendern: (id, data) => api.put(`/superchat/wissensbasis/${id}`, data),
  wissensbasisLoeschen: (id) => api.delete(`/superchat/wissensbasis/${id}`),
  wissensbasisFbcLaden: (mandant) => api.post('/superchat/wissensbasis/fbc-methodik', null, { params: { mandant } }),
  wissensbasisZinzinoLaden: (mandant) => api.post('/superchat/wissensbasis/zinzino-fakten', null, { params: { mandant } }),
  wissensbasisZinzinoInternLaden: (mandant) => api.post('/superchat/wissensbasis/zinzino-intern', null, { params: { mandant } }),
  agentConfig: (mandant) => api.get(`/superchat/agent-config/${mandant}`),
  agentConfigSchreiben: (mandant, data) => api.put(`/superchat/agent-config/${mandant}`, data),
  auswertung: (params) => api.get('/superchat/auswertung', { params }),
  health: () => api.get('/superchat/health'),
  metaConfig: () => api.get('/superchat/meta-config'),
  // WhatsApp-Bruecke (verknuepftes Geraet an der Handynummer)
  brueckeZustand: () => api.get('/wa-bruecke/zustand'),
  brueckeKonfig: (data) => api.put('/wa-bruecke/konfiguration', data),
  brueckeNeuKoppeln: () => api.post('/wa-bruecke/neu-koppeln'),
  metaConfigSchreiben: (data) => api.put('/superchat/meta-config', data),
  warteliste: (params) => api.get('/superchat/warteliste', { params }),
  wartelisteAnlegen: (data) => api.post('/superchat/warteliste', data),
  wartelisteEntfernen: (id) => api.delete(`/superchat/warteliste/${id}`),
  terminangebote: (params) => api.get('/superchat/terminangebote', { params }),
  absageRuecklauf: (konvId) => api.post(`/superchat/konversationen/${konvId}/absage-ruecklauf`),
};

// Instagram-Direktnachrichten im Posteingang (30.10.2026, backend/instagram_dm.py)
export const instagramAPI = {
  status: () => api.get('/instagram/status'),
  konfig: (data) => api.put('/instagram/konfig', data),
  verbinden: () => api.post('/instagram/verbinden'),
  trennen: () => api.post('/instagram/trennen'),
};

export const schnellerfassungAPI = {
  verstehen: (text) => api.post('/schnellerfassung/verstehen', { text }),
  kurse: () => api.get('/schnellerfassung/kurse'),
  slots: (params) => api.get('/schnellerfassung/slots', { params }),
  buchen: (data) => api.post('/schnellerfassung/buchen', data),
};

/* Eventrechner (27.10.2026): Szenarien, Sponsoren, Kosten Plan/Ist, Abgleich mit dem Ticketmodul */
export const eventrechnerAPI = {
  liste: () => api.get('/eventrechner'),
  ticketEvents: () => api.get('/eventrechner/ticket-events'),
  lesen: (id) => api.get(`/eventrechner/${id}`),
  anlegen: (data) => api.post('/eventrechner', data),
  aendern: (id, data) => api.put(`/eventrechner/${id}`, data),
  loeschen: (id) => api.delete(`/eventrechner/${id}`),
  sponsorAnlegen: (id, data) => api.post(`/eventrechner/${id}/sponsoren`, data),
  sponsorAendern: (id, sid, data) => api.put(`/eventrechner/${id}/sponsoren/${sid}`, data),
  sponsorLoeschen: (id, sid) => api.delete(`/eventrechner/${id}/sponsoren/${sid}`),
};

export const ticketsCrmAPI = {
  uebersicht: (slug) => api.get(`/tickets-crm/${slug}/uebersicht`),
  manuell: (slug, data) => api.post(`/tickets-crm/${slug}/manuell`, data),
  bezahlt: (slug, id) => api.post(`/tickets-crm/${slug}/${id}/bezahlt`),
  unbezahlt: (slug, id) => api.post(`/tickets-crm/${slug}/${id}/unbezahlt`),
  storno: (slug, id) => api.post(`/tickets-crm/${slug}/${id}/storno`),
  einlassZurueck: (slug, id) => api.post(`/tickets-crm/${slug}/${id}/einlass-zurueck`),
  streamLesen: (slug) => api.get(`/public/tickets/${slug}/stream`),
  streamSetzen: (slug, body) => api.post(`/tickets-crm/${slug}/stream`, body),
  mail: (slug, id) => api.post(`/tickets-crm/${slug}/${id}/mail`),
  emailAendern: (slug, id, email) => api.post(`/tickets-crm/${slug}/${id}/email`, { email }),
  exportCSV: (slug) => api.get(`/tickets-crm/${slug}/export.csv`, { responseType: 'blob' }),
  scannerLink: (slug) => api.get(`/tickets-crm/${slug}/scanner-link`),
};

export const seminareAPI = {
  uebersicht: () => api.get('/seminare/uebersicht'),
  anmelden: (data) => api.post('/seminare/anmeldung', data),
  absagen: (id, grund = '') => api.patch(`/seminare/anmeldung/${id}/absagen`, { grund }),
  wiederAnmelden: (id) => api.patch(`/seminare/anmeldung/${id}/wieder-anmelden`),
  exportCSV: () => api.get('/seminare/export.csv', { responseType: 'blob' }),
};

// Gürtelprüfung (28.10.2026, backend/pruefungsanmeldung.py) — Rechte wie die Seminare
export const pruefungenAPI = {
  uebersicht: () => api.get('/pruefungen/uebersicht'),
  anmelden: (pid, memberId, tshirtGroesse) => api.post(`/pruefungen/${pid}/anmeldung`, { member_id: memberId, tshirt_groesse: tshirtGroesse }),
  absagen: (id, grund = '') => api.patch(`/pruefungen/anmeldung/${id}/absagen`, { grund }),
  postenNachholen: (id) => api.post(`/pruefungen/anmeldung/${id}/posten`),
  exportCSV: () => api.get('/pruefungen/export.csv', { responseType: 'blob' }),
};

// Beratung (intern, CRM-Login)
export const beratungAdminAPI = {
  list: (params) => api.get('/beratung', { params }),
  complete: (id) => api.patch(`/beratung/${id}/abschliessen`),
  cancel: (id) => api.patch(`/beratung/${id}/absagen`),
  qr: () => api.get('/beratung/qr'),
};

// Team
export const teamAPI = {
  list: () => api.get('/team'),
  updateRole: (userId, role) => api.put(`/team/${userId}/role`, { role }),
  createUser: (daten) => api.post('/team/users', daten),
  setPassword: (userId, password) => api.put(`/team/${userId}/password`, { password }),
};

// Reports
export const reportsAPI = {
  leadConversion: (params) => api.get('/reports/lead-conversion', { params }),
  agentPerformance: (params) => api.get('/reports/agent-performance', { params }),
  revenue: (params) => api.get('/reports/revenue', { params }),
  leadSourceROI: (params) => api.get('/reports/lead-source-roi', { params }),
  parking: (params) => api.get('/reports/parking', { params }),
  exportExcel: (type, params) => `${API}/reports/export/excel/${type}?time_range=${params?.time_range || '30d'}`,
  exportPDF: (type, params) => `${API}/reports/export/pdf/${type}?time_range=${params?.time_range || '30d'}`,
};

// RBAC
export const rbacAPI = {
  getMyPermissions: () => api.get('/rbac/my-permissions'),
  getPermissions: () => api.get('/rbac/permissions'),
  getRoles: () => api.get('/rbac/roles'),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (id, data) => api.put(`/rbac/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/rbac/roles/${id}`),
  assignRole: (userId, role) => api.put(`/rbac/users/${userId}/role`, { role }),
};

// Calendar
export const calendarAPI = {
  getEvents: (params) => api.get('/calendar', { params }),
};

// SMS über das Vertriebshandy (06.10.2026): Nummer + fertige Texte holen, Übergabe an die Nachrichten-App festhalten
export const smsHandyAPI = {
  vorschlag: (params) => api.get('/sms-handy/vorschlag', { params }),
  protokoll: (daten, optionen) => api.post('/sms-handy/protokoll', daten, optionen),
  zuruecknehmen: (id) => api.delete(`/sms-handy/protokoll/${id}`),
};

// Kalender-Abo: eigener Kalender per iCal-Link (z. B. OptiOffice), Freigabe an einzelne Nutzer
export const kalenderAboAPI = {
  lesen: () => api.get('/kalender-abo'),
  speichern: (daten) => api.put('/kalender-abo', daten),
  abrufen: () => api.post('/kalender-abo/abrufen'),
  entfernen: () => api.delete('/kalender-abo'),
  uebertragen: (daten) => api.post('/kalender-abo/uebertragen', daten),   // 29.10.2026: Termin an einen Trainer geben
};

// Integrations
export const integrationsAPI = {
  get: () => api.get('/integrations'),
  update: (data) => api.put('/integrations', data),
  testConnection: () => api.post('/integrations/test-connection'),
  sendSMS: (data) => api.post('/integrations/sms/send', data),
  sendWhatsApp: (data) => api.post('/integrations/whatsapp/send', data),
  getMessages: (params) => api.get('/integrations/messages', { params }),
  // Email
  getEmailConfig: () => api.get('/integrations/email/config'),
  updateEmailConfig: (data) => api.put('/integrations/email/config', data),
  testEmailConnection: () => api.post('/integrations/email/test-connection'),
  testBrevo: () => api.post('/integrations/email/test-brevo'),
  brevoWebhook: () => api.post('/integrations/email/brevo-webhook'),
  sendEmail: (data) => api.post('/integrations/email/send', data),
  bulkEmailPreview: (data) => api.post('/integrations/email/bulk/preview', data),
  bulkEmailSend: (data) => api.post('/integrations/email/bulk/send', data),
  // Nuki
  getNukiConfig: () => api.get('/integrations/nuki'),
  updateNukiConfig: (data) => api.put('/integrations/nuki', data),
  testNukiConnection: () => api.post('/integrations/nuki/test-connection'),
  listNukiSmartlocks: () => api.get('/integrations/nuki/smartlocks'),
  getTuerAnleitung: () => api.get('/tuer-anleitung/uebersicht'),   // wer hat die Abschließ-Anleitung bestätigt (22.10.2026)
  // Stripe
  getStripeConfig: () => api.get('/integrations/stripe'),
  updateStripeConfig: (data) => api.put('/integrations/stripe', data),
  testStripeConnection: () => api.post('/integrations/stripe/test-connection'),
  // OneClickGym
  getOneClickGymConfig: () => api.get('/integrations/oneclickgym'),
  updateOneClickGymConfig: (data) => api.put('/integrations/oneclickgym', data),
  regenerateOneClickGymSecret: () => api.post('/integrations/oneclickgym/regenerate-secret'),
};

// Automations
export const automationsAPI = {
  getRules: () => api.get('/automations/rules'),
  createRule: (data) => api.post('/automations/rules', data),
  updateRule: (id, data) => api.put(`/automations/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/automations/rules/${id}`),
  updateGlobalLimits: (data) => api.put('/automations/global-limits', data),
  getLog: (params) => api.get('/automations/log', { params }),
  process: () => api.post('/automations/process'),
};

// Listing Feeds
// Check-ins
// Ernährungscoaching auf der Trainer-Webseite /coach (15.10.2026): Dieselben Aufrufe (ernaehrungAPI, gewohnheitenAPI,
// aufgabenAPI.erledigt, checkinsAPI.suche, bildSrc) laufen dort über den Trainer-Client gegen den Spiegel
// /api/coach/x/… (backend/coach/ernaehrung_spiegel.py). CoachPage setzt beim Betreten setErnaehrungClient(coach, '/x')
// und beim Verlassen clearErnaehrungClient(); die Aufrufer bleiben unverändert. Für <img>/<a> (kein Token-Header)
// holt der Umschalter einen kurzlebigen Bild-Token (GET {praefix}/bild-token) und bildSrc hängt ihn als ?t= an.
let ernaehrungClient = null;
let ernaehrungPraefix = '';
let ernaehrungBildToken = null;
let ernaehrungBildTimer = null;

const bildTokenHolen = async () => {
  if (!ernaehrungClient || !ernaehrungPraefix) return;
  try {
    const r = await ernaehrungClient.get(`${ernaehrungPraefix}/bild-token`);
    ernaehrungBildToken = r.data?.token || null;
  } catch { ernaehrungBildToken = null; }
};

export function setErnaehrungClient(client, praefix = '') {
  ernaehrungClient = client || null;
  ernaehrungPraefix = praefix || '';
  ernaehrungBildToken = null;
  if (ernaehrungBildTimer) { clearInterval(ernaehrungBildTimer); ernaehrungBildTimer = null; }
  if (ernaehrungClient && ernaehrungPraefix) {
    bildTokenHolen();
    ernaehrungBildTimer = setInterval(bildTokenHolen, 10 * 60 * 1000);   // Token gilt 20 Minuten
  }
}

export function clearErnaehrungClient() { setErnaehrungClient(null, ''); }
export const ernaehrungClientAktiv = () => Boolean(ernaehrungClient);
export const setErnaehrungBildToken = (t) => { ernaehrungBildToken = t || null; };

const ern = () => ernaehrungClient || api;
const ernPfad = (pfad) => `${ernaehrungPraefix}${pfad}`;

// Video-/Audio-Feedback (Coach-Cockpit W2.6, 29.10.2026): CRM und — über den Ernährungs-Umschalter — der Spiegel
// /api/coach/x/medien-feedback. Abspielen nur als Blob mit Anmeldung (nie eine öffentliche Adresse).
export const medienFeedbackAPI = {
  liste: (leadId, params) => ern().get(ernPfad(`/medien-feedback/mitglied/${leadId}`), { params }),
  hochladen: (leadId, fd, cfg = {}) => ern().post(ernPfad(`/medien-feedback/mitglied/${leadId}`), fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000, ...cfg }),
  datei: (leadId, fid) => ern().get(ernPfad(`/medien-feedback/mitglied/${leadId}/${fid}/datei`), { responseType: 'blob', timeout: 180000 }),
  zurueckziehen: (leadId, fid) => ern().delete(ernPfad(`/medien-feedback/mitglied/${leadId}/${fid}`)),
  einstellung: () => ern().get(ernPfad('/medien-feedback/einstellung')),
  einstellungSetzen: (monate) => ern().put(ernPfad('/medien-feedback/einstellung'), { aufbewahrung_monate: monate }),
};

export const checkinsAPI = {
  checkin: (data) => api.post('/checkins', data),
  checkout: (memberId) => api.post('/checkins/checkout', { member_id: memberId }),
  present: () => api.get('/checkins/present'),
  // Personensuche am Empfang: alle Leads (auch fremde und archivierte), serverseitig, nach Wortteilen
  // Zeitlimit: am Tresen darf „Sucht…" nicht stehen bleiben, wenn die Verbindung hängt
  suche: (q, limit = 15) => ern().get(ernPfad('/checkins/suche'), { params: { q, limit }, timeout: 10000 }),
  getQRCode: (memberId) => api.get(`/checkins/qr/${memberId}`),
  getStudioQRCode: () => api.get('/checkins/qr/studio'),
  getToday: () => api.get('/checkins/today'),
  getStats: () => api.get('/checkins/stats'),
  getMemberCheckins: (memberId) => api.get(`/checkins/member/${memberId}`),
};

// Memberships
export const membershipsAPI = {
  getPlans: () => api.get('/memberships/plans'),
  updatePlan: (id, data) => api.put(`/memberships/plans/${id}`, data),
  assign: (data) => api.post('/memberships/assign', data),
  getAll: (status) => api.get('/memberships', { params: status ? { status } : {} }),
  beitrag: (id, monatsbeitrag, bemerkung) => api.patch(`/memberships/${id}/beitrag`, { monatsbeitrag, bemerkung }),
  member: (leadId) => api.get(`/memberships/member/${leadId}`),
  bestandStand: () => api.get('/vertraege/bestand/stand'),
  bestandVorlage: (leadId) => api.get(`/vertraege/bestand/vorlage/${leadId}`),
  bestandBestaetigen: (daten) => api.post('/vertraege/bestand', daten),
  neueLaufzeitVorlage: (leadId) => api.get(`/vertraege/bestand/neue-laufzeit/vorlage/${leadId}`),
  neueLaufzeit: (daten) => api.post('/vertraege/bestand/neue-laufzeit', daten),
  papierStatus: (membershipId, papier_original) => api.patch(`/vertraege/bestand/papier/${membershipId}`, { papier_original }),
  upsellBausteine: (id) => api.get(`/memberships/${id}/upsell/bausteine`),
  upsell: (id, daten) => api.post(`/memberships/${id}/upsell`, daten),
  einmalposten: (id, daten) => api.post(`/memberships/${id}/einmalposten`, daten),
  upsellBeenden: (id, nr, ab) => api.post(`/memberships/${id}/upsell/${nr}/beenden`, { ab }),
  upsellPdfUrl: (id, nr) => `${API}/memberships/${id}/upsell/${nr}/pdf`,
  entfernen: (id, grund) => api.post(`/memberships/${id}/entfernen`, { grund }),
  kuendigungsDuplikate: () => api.get('/memberships/gekuendigt/duplikate'),
  kuendigungenZusammenlegen: () => api.post('/memberships/gekuendigt/zusammenlegen'),
  getMember: (memberId) => api.get(`/memberships/member/${memberId}`),
  cancel: (id, body) => api.patch(`/memberships/${id}/cancel`, body),
  // Kündigen und Planwechsel laufen über den Vertrag (30.10.2026): hier fragt die Oberfläche, wohin
  vertrag: (id) => api.get(`/memberships/${id}/vertrag`),
  pause: (id, body) => api.post(`/memberships/${id}/pause`, body),
  pauseAufheben: (id) => api.delete(`/memberships/${id}/pause`),
  gymVereinbaren: (id) => api.post(`/memberships/${id}/gym-vereinbarung`),
  gymVereinbarungBeenden: (id) => api.delete(`/memberships/${id}/gym-vereinbarung`),
  planwechsel: (id, body) => api.post(`/memberships/${id}/planwechsel`, body),
  markPaid: (id) => api.patch(`/memberships/${id}/payment`),
  getStats: () => api.get('/memberships/stats'),
};

// Vertrag ändern (Leitung, 10.10.2026): Berichtigung von Name/Geburtsdatum und Zahlerwechsel.
// Der Zahlerwechsel selbst läuft über die Unterschrift-Station (unterschriftAPI.senden, art 'zahlerwechsel').
export const vertragAenderungAPI = {
  berichtigungVorlage: (id) => api.get(`/vertraege/${id}/berichtigung/vorlage`),
  berichtigen: (id, daten) => api.post(`/vertraege/${id}/berichtigen`, daten),
  zahlerwechselVorlage: (id) => api.get(`/vertraege/${id}/zahlerwechsel/vorlage`),
};

// Payments
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getTransactions: () => api.get('/payments/transactions'),
};

// Training / Trainingshistorie
export const trainingAPI = {
  getMemberHistory: (memberId, params) => api.get(`/training/member/${memberId}`, { params }),
  create: (data) => api.post('/training', data),
  delete: (id) => api.delete(`/training/${id}`),
};

// Shop / Warenwirtschaft (WWS)
export const shopAPI = {
  // Dashboard
  dashboard: () => api.get('/shop/dashboard'),
  // Produkte
  listProducts: (params) => api.get('/shop/products', { params }),
  getProduct: (id) => api.get(`/shop/products/${id}`),
  createProduct: (data) => api.post('/shop/products', data),
  updateProduct: (id, data) => api.put(`/shop/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/shop/products/${id}`),
  getCategories: () => api.get('/shop/categories'),
  // Bestand
  correctStock: (id, data) => api.post(`/shop/products/${id}/stock`, data),
  listMovements: (params) => api.get('/shop/stock-movements', { params }),
  getLowStock: () => api.get('/shop/low-stock'),
  // Reservierungen
  listReservations: (params) => api.get('/shop/reservations', { params }),
  cancelReservation: (id) => api.delete(`/shop/reservations/${id}`),
  // Bestellungen
  listOrders: (params) => api.get('/shop/orders', { params }),
  getOrder: (id) => api.get(`/shop/orders/${id}`),
  createOrder: (data) => api.post('/shop/orders', data),
  confirmOrder: (id) => api.put(`/shop/orders/${id}/confirm`),
  completeOrder: (id) => api.put(`/shop/orders/${id}/complete`),
  cancelOrder: (id) => api.put(`/shop/orders/${id}/cancel`),
};

// Prämien (CRM-Verwaltung — Gegenstück zum app-seitigen Prämiensystem)
export const rewardsAPI = {
  catalog: () => api.get('/rewards/catalog'),
  createReward: (data) => api.post('/rewards/catalog', data),
  updateReward: (id, data) => api.put(`/rewards/catalog/${id}`, data),
  deactivateReward: (id) => api.delete(`/rewards/catalog/${id}`),
  redemptions: (params) => api.get('/rewards/redemptions', { params }),
  fulfill: (code) => api.post('/rewards/redemptions/fulfill', { code }),
  cancelRedemption: (id) => api.patch(`/rewards/redemptions/${id}/cancel`),
};

// Familie (Eltern-/Kinder-Verwaltung): Verknüpfen ist Empfangssache
// Stammplatz-QR: Empfang erzeugt am Lead einen Code zur Kurs-Selbstwahl
export const stammplatzAPI = {
  createInvite: (leadId, maxPlaetze = 1) =>
    api.post('/public/admin/stammplatz-invite', { lead_id: leadId, max_plaetze: maxPlaetze }),
};

// Kids-Challenge (Kids Launch Herbst 2026): Zahlungslink, Stand, bestanden, Erstattung
export const challengeAPI = {
  artikel: () => api.get('/challenge/artikel'),
  alle: () => api.get('/challenge'),
  vonLead: (leadId) => api.get(`/challenge/${leadId}`),
  zahlungslink: (leadId, track) => api.post(`/challenge/${leadId}/zahlungslink`, { track }),
  bestanden: (leadId, bestanden) => api.post(`/challenge/${leadId}/bestanden`, { bestanden }),
  erstatten: (leadId, betrag, grund) => api.post(`/challenge/${leadId}/erstatten`, { betrag, grund }),
};

export const familyAPI = {
  ofLead: (leadId) => api.get(`/public/family/admin/of-lead/${leadId}`),
  link: (profilId, accountId, rolle = 'verwalter') => api.post('/public/family/admin/link', { profil_id: profilId, account_id: accountId, rolle }),
  unlink: (profilId, accountId) => api.delete('/public/family/admin/link', { params: { profil_id: profilId, account_id: accountId } }),
  recordCheckup: (profilId, datum) => api.post(`/public/family/admin/checkup/${profilId}`, { datum }),
  // Karte „Check-Up“ (11.10.2026): eingetragene Tage, Kontingent, laufende Anfrage; Tag korrigieren (zählt nicht neu)
  checkupStand: (profilId) => api.get(`/public/family/admin/checkup/${profilId}`),
  checkupVerlegen: (profilId, von, nach) => api.patch(`/public/family/admin/checkup/${profilId}`, { von, nach }),
};

// Graduierungsreife (Trainer meldet das Backend, Graduierung bleibt manuell)
// Graduierungs-Regelwerk (22.09.2026): Übersicht, Status, Streifen, direkte Beförderung
export const graduierungAPI = {
  uebersicht: () => api.get('/graduierung/uebersicht'),
  status: (memberId) => api.get(`/graduierung/${memberId}`),
  streifen: (memberId, anzahl = 1) => api.post(`/graduierung/${memberId}/streifen`, { anzahl }),
  graduieren: (memberId, stufe) => api.post(`/graduierung/${memberId}/graduieren/${stufe}`),
  stufen: () => api.get('/graduierung/stufen'),
  einstufen: (memberId, stufe, einheiten) => api.post(`/graduierung/${memberId}/einstufen`, { stufe, einheiten }),
  urkundeUrl: (memberId, graduierungId) => `/graduierung/${memberId}/urkunde/${graduierungId}`,
  // Octiv-Altbestand (28.09.2026)
  octivNachtrag: (datei, apply) => {
    const fd = new FormData(); fd.append('datei', datei, datei.name || 'octiv.csv'); fd.append('apply', apply ? 'true' : 'false');
    return api.post('/graduierung/octiv-nachtrag', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
  },
  vorschlaege: () => api.get('/graduierung/vorschlaege'),
  vorschlaegeBerechnen: () => api.post('/graduierung/vorschlaege/berechnen', {}, { timeout: 180000 }),
  vorschlagUebernehmen: (id, stufe) => api.post(`/graduierung/vorschlaege/${id}/uebernehmen`, stufe ? { stufe } : {}),
  vorschlagVerwerfen: (id) => api.post(`/graduierung/vorschlaege/${id}/verwerfen`),
};

// Dokumentenablage am Mitglied (22.09.2026)
// Rechnungen je Mitglied (15.10.2026): beide Quellen (Rechnungslauf + Lexware-Spiegel), Recht finanzen.view
export const mitgliedRechnungenAPI = {
  liste: (leadId) => api.get(`/rechnungen/mitglied/${leadId}`),
  pdf: (leadId, id) => api.get(`/rechnungen/mitglied/${leadId}/${id}/pdf`, { responseType: 'blob' }),
  senden: (leadId, id) => api.post(`/rechnungen/mitglied/${leadId}/${id}/senden`),
  finanzkarte: (leadId) => api.get(`/rechnungen/mitglied/${leadId}/finanzkarte`),
};

export const dokumenteAPI = {
  liste: (leadId) => api.get(`/dokumente/${leadId}`),
  hochladen: (leadId, datei, typ, notiz) => { const fd = new FormData(); fd.append('datei', datei); fd.append('typ', typ); if (notiz) fd.append('notiz', notiz); return api.post(`/dokumente/${leadId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  loeschen: (id) => api.delete(`/dokumente/${id}`),
};

// Trainer-Anwesenheit im CRM (22.09.2026)
export const trainerHeuteAPI = {
  heute: (datum) => api.get('/trainer-heute', { params: datum ? { datum } : {} }),
  status: (buchungId, status) => api.post(`/trainer-heute/buchung/${buchungId}/status`, { status }),
  hinzufuegen: (courseId, memberId) => api.post(`/trainer-heute/kurs/${courseId}/hinzufuegen/${memberId}`),
  notiz: (memberId, text) => api.post(`/trainer-heute/mitglied/${memberId}/notiz`, { text }),
  notizen: (memberId) => api.get(`/trainer-heute/mitglied/${memberId}/notizen`),
  schnellerfassung: (courseId, body) => api.post(`/trainer-heute/kurs/${courseId}/schnellerfassung`, body),
  // Reinsprechen (28.09.2026): Aufnahme oder Text -> Treffer; dann alle bestaetigten auf einmal anwesend
  sprache: (courseId, { audio, text }) => {
    const fd = new FormData();
    if (audio) fd.append('audio', audio, audio.name || 'anwesenheit.webm');
    if (text) fd.append('text', text);
    return api.post(`/trainer-heute/kurs/${courseId}/sprache`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  anwesendMehrere: (courseId, memberIds) => api.post(`/trainer-heute/kurs/${courseId}/anwesend-mehrere`, { member_ids: memberIds }),
};

// Automatiken (22.09.2026): Schalter + Wortlaut aller fest verdrahteten Abläufe
export const automatikenAPI = {
  liste: () => api.get('/automatiken'),
  setzen: (key, daten) => api.patch(`/automatiken/${key}`, daten),
};

// Rundschreiben (22.09.2026)
export const rundschreibenAPI = {
  optionen: () => api.get('/rundschreiben/optionen'),
  vorschau: (daten) => api.post('/rundschreiben/vorschau', daten),
  anlegen: (daten) => api.post('/rundschreiben', daten),
  liste: () => api.get('/rundschreiben'),
  vorschauHtml: (id) => api.get(`/rundschreiben/${id}/vorschau.html`, { responseType: 'text' }),
  zustellung: (id) => api.get(`/rundschreiben/${id}/zustellung`),
  testmail: (id, an) => api.post(`/rundschreiben/${id}/testmail`, an ? { an } : {}),
  testsms: (id, an) => api.post(`/rundschreiben/${id}/testsms`, { an }),
  empfaengerAktualisieren: (id) => api.post(`/rundschreiben/${id}/empfaenger-aktualisieren`),
  // Brevo-Abgleich (06.10.2026): Konto (Guthaben) und Zustellstand je Nachricht aus der Brevo-API
  brevoKonto: (neu = false) => api.get('/rundschreiben/brevo-konto', { params: neu ? { neu: 1 } : {} }),
  abgleich: (id, uebernehmen = false) => api.post(`/rundschreiben/${id}/abgleich`, { uebernehmen }),
};

export const graduationAPI = {
  list: (status = 'offen') => api.get('/public/admin/graduation-ready', { params: { status } }),
  // Status wird als Query-Parameter erwartet, daher kein Body (null)
  resolve: (id, status) => api.patch(`/public/admin/graduation-ready/${id}`, null, { params: { status } }),
};

// PT-Aufträge (Personal Training)
export const ptOrdersAPI = {
  listOpen: () => api.get('/pt-orders/open'),
  listMine: () => api.get('/pt-orders/mine'),
  accept: (id, data) => api.post(`/pt-orders/${id}/accept`, data),
  complete: (id) => api.patch(`/pt-orders/${id}/complete`),
  cancel: (id) => api.patch(`/pt-orders/${id}/cancel`),
  noShow: (id) => api.patch(`/pt-orders/${id}/no-show`),
};

// DSGVO (Datenschutz)
export const dsgvoAPI = {
  exportData: (leadId) => api.get(`/dsgvo/export/${leadId}`),
  deleteData: (leadId) => api.delete(`/dsgvo/delete/${leadId}`),
  auditLog: (params) => api.get('/dsgvo/audit-log', { params }),
  getConsent: (leadId) => api.get(`/dsgvo/consent/${leadId}`),
  setConsent: (leadId, granted, source = 'crm') => api.post(`/dsgvo/consent/${leadId}`, { granted, source }),
  listRequests: (params) => api.get('/dsgvo/requests', { params }),
  createRequest: (data) => api.post('/dsgvo/requests', data),
  reviewRequest: (id, action, notes = '') => api.patch(`/dsgvo/requests/${id}`, { action, notes }),
  retentionReview: () => api.get('/dsgvo/retention-review'),
  // Einwilligungen/Waiver (Feature 2)
  consentTexts: () => api.get('/dsgvo/consent-texts'),
  updateConsentText: (type, body) => api.put(`/dsgvo/consent-texts/${type}`, { body }),
  recordConsent: (data) => api.post('/dsgvo/consent-record', data),
  consentOverview: (leadId) => api.get(`/dsgvo/consent-overview/${leadId}`),
  consentWiderruf: (data) => api.post('/dsgvo/consent-widerruf', data),
};

// Teilnehmerliste in der App („Wer kommt noch?“): zwei Betreiber-Schalter, beide Standard
// aus. Lesen mit datenschutz.consent_manage, Schreiben nur Inhaber — beide Aufrufe
// antworten mit demselben Stand samt Übersicht.
// Je Mitglied (Lead-Seite): Stand lesen ({aktiv, moeglich, sichtbar, hinweis_gesehen,
// ausgeblendet, grund}) und den Vornamen aus- bzw. wieder einblenden.
export const teilnehmerlisteAPI = {
  get: () => api.get('/teilnehmerliste/einstellungen'),
  put: (werte) => api.put('/teilnehmerliste/einstellungen', werte),
  mitglied: (leadId) => api.get(`/teilnehmerliste/mitglied/${leadId}`),
  mitgliedSetzen: (leadId, ausgeblendet) => api.put(`/teilnehmerliste/mitglied/${leadId}`, { ausgeblendet }),
};

// Probetrainings-Funnels (backend/funnels) — Conversion & Absprünge
export const funnelsAPI = {
  definition: (slug) => api.get(`/public/funnel/${slug}`),
  statistik: (slug, tage = 30, extra = {}) =>
    api.get(`/funnel/${slug}/statistik`, { params: { tage, ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v != null && v !== '')) } }),
  abbrecher: (funnel, limit = 50) =>
    api.get('/funnel/sitzungen', { params: { funnel, nur_abbrecher: true, limit } }),
  kidsLaunch: (tage = 30) => api.get('/kids-launch/auswertung', { params: { tage } }),
};

// Setter-Cockpit (routes/vertrieb.py)
export const vertriebAPI = {
  cockpit: () => api.get('/vertrieb/cockpit'),
  versuch: (leadId, daten) => api.post(`/vertrieb/${leadId}/versuch`, daten),
  qualifizierung: (leadId, daten) => api.post(`/vertrieb/${leadId}/qualifizierung`, daten),
  closer: () => api.get('/vertrieb/closer'),
  anwesenheit: (leadId, daten) => api.post(`/vertrieb/${leadId}/anwesenheit`, daten),
  gespraech: (leadId, daten) => api.post(`/vertrieb/${leadId}/gespraech`, daten),
  kennzahlen: (wochen = 8) => api.get('/vertrieb/kennzahlen', { params: { wochen } }),
  uebergabe: (leadId, daten) => api.post(`/vertrieb/${leadId}/uebergabe`, daten),
  angebotAnlegen: (leadId, daten) => api.post(`/vertrieb/${leadId}/angebot`, daten),
  angebote: (status = 'offen') => api.get('/vertrieb/angebote', { params: { status } }),
  angebotEntscheiden: (angebotId, daten) => api.patch(`/vertrieb/angebot/${angebotId}`, daten),
};

// Sommerfest-Festkarte (routes/fest.py)
export const festAPI = {
  // öffentlich (Gast)
  publicConfig: () => api.get('/public/fest/config'),
  karteAnlegen: (data) => api.post('/public/fest/karte', data),
  karteOeffentlich: (code) => api.get(`/public/fest/karte/${code}`),
  // 12.09.-Erweiterung: Gast bucht sein Probetraining selbst von der Karte
  gastKurse: (code, alter) => api.get(`/public/fest/karte/${code}/kurse`, { params: alter ? { alter } : {} }),
  gastBuchen: (code, daten) => api.post(`/public/fest/karte/${code}/probetraining`, daten),
  // Founding "Small Group": Zaehler und Reservierung. Beide Wege — Gast auf
  // seiner Karte, Team mit Login — landen auf demselben Deckel.
  foundingStand: () => api.get('/public/fest/founding'),
  foundingSichernGast: (code) => api.post(`/public/fest/karte/${code}/founding`, {}),
  foundingSichernTeam: (code) => api.post(`/fest/karte/${code}/founding`, {}),
  foundingListe: () => api.get('/fest/founding'),
  foundingCSV: () => api.get('/fest/founding.csv', { responseType: 'blob' }),
  gastStorno: (code, buchungId) => api.delete(`/public/fest/karte/${code}/probetraining/${buchungId}`),
  suche: (q) => api.get('/fest/suche', { params: { q } }),
  nachfassliste: () => api.get('/fest/nachfassliste'),
  qrUrl: (url) => `${API}/public/fest/qr?url=${encodeURIComponent(url)}`,
  // Mitarbeiter
  karte: (code) => api.get(`/fest/karte/${code}`),
  kartePerLos: (nr) => api.get(`/fest/karte/los/${nr}`),
  // Eingang: Gast ist da. Idempotent — zweimal scannen schadet nicht.
  checkin: (code, data = {}) => api.post(`/fest/karte/${code}/checkin`, data),
  challenge: (code, data) => api.post(`/fest/karte/${code}/challenge`, data),
  kurse: (interesse = '') => api.get('/fest/kurse', { params: { interesse } }),
  probetraining: (code, data) => api.post(`/fest/karte/${code}/probetraining`, data),
  gymfights: (code, data) => api.post(`/fest/karte/${code}/gymfights`, data),
  uebersicht: () => api.get('/fest/uebersicht'),
  verlosungZiehen: () => api.post('/fest/verlosung/ziehen'),
  verlosungPreis: (karten_code, preis) => api.post('/fest/verlosung/preis', { karten_code, preis }),
  exportCSV: () => api.get('/fest/export.csv', { responseType: 'blob' }),
  nachfass: (data = {}) => api.post('/fest/nachfass', data),
};

// Processors / AVV
export const processorsAPI = {
  list: () => api.get('/processors'),
  listPublic: () => api.get('/processors/public'),
  create: (data) => api.post('/processors', data),
  update: (id, data) => api.put(`/processors/${id}`, data),
  remove: (id) => api.delete(`/processors/${id}`),
};

// WhatsApp-KI-Setter (Entwuerfe pruefen, senden, uebernehmen)
// Freigabe-Zentrale (v5, M4): eine Warteschlange fuer alle Entwuerfe
// Pia-Finanzen (02.11.2026, backend/pia_finanzen/router.py): Abbuchungswunsch (Team) und Einstellungen/offene Posten (nur Inhaber)
export const piaFinanzenAPI = {
  abbuchungswunschLage: (leadId) => api.get('/pia-finanzen/abbuchungswunsch', { params: { lead_id: leadId } }),
  abbuchungswunsch: (daten) => api.post('/pia-finanzen/abbuchungswunsch', daten),
  einstellungen: () => api.get('/pia-finanzen/einstellungen'),
  einstellungenSetzen: (daten) => api.put('/pia-finanzen/einstellungen', daten),
  offenePosten: (monat) => api.get('/pia-finanzen/offene-posten', { params: monat ? { monat } : {} }),
};

export const freigabenAPI = {
  liste: (params) => api.get('/freigaben', { params }),
  einzeln: (id) => api.get(`/freigaben/${id}`),
  anzahl: () => api.get('/freigaben/anzahl'),
  statistik: (params) => api.get('/freigaben/statistik', { params }),
  freigeben: (id, text, extra = {}) => api.post(`/freigaben/${id}/freigeben`, { text, ...extra }),
  verwerfen: (id, grund, kommentar) => api.post(`/freigaben/${id}/verwerfen`, { grund, kommentar }),
  zurueckstellen: (id, bis) => api.post(`/freigaben/${id}/zurueckstellen`, { bis }),
  manuellGesendet: (id) => api.post(`/freigaben/${id}/manuell-gesendet`),
  // 30.10.2026 (freigaben/schritte.py, bilanz.py): die Knöpfe neben „Senden“, „Bisher“ je Lead, Tagesabschluss + Kennzahl
  diesmalNicht: (id, grund, tage) => api.post(`/freigaben/${id}/diesmal-nicht`, { grund, tage }),
  andersErledigt: (id, art, notiz) => api.post(`/freigaben/${id}/anders-erledigt`, { art, notiz }),
  leadBeenden: (id, grund) => api.post(`/freigaben/${id}/lead-beenden`, { grund }),
  verlauf: (id) => api.get(`/freigaben/${id}/verlauf`),
  bilanz: (tag) => api.get('/freigaben/bilanz', { params: tag ? { tag } : {} }),
};

// Kurskontingent je Tarif (16.10.2026, backend/kurskontingent.py): „Kurse diese Woche: x von y (z nachholbar)“
export const kurskontingentAPI = {
  stand: (leadId, datum) => api.get(`/kurskontingent/${leadId}`, { params: datum ? { datum } : {} }),
};

export const einschaetzungAPI = {
  lesen: (leadId) => api.get(`/leads/${leadId}/einschaetzung`),
  neu: (leadId) => api.post(`/leads/${leadId}/einschaetzung/neu`),
  aktionUebernehmen: (leadId) => api.post(`/leads/${leadId}/einschaetzung/aktion-uebernehmen`),
  uebersicht: (params) => api.get('/einschaetzungen/uebersicht', { params }),
};

export const angebotAPI = {
  entwurf: (leadId) => api.post(`/leads/${leadId}/angebot/entwurf`),
  lesen: (id) => api.get(`/angebote/${id}`),
  verlaengern: (id, tage = 7) => api.post(`/angebote/${id}/verlaengern`, { tage }),
};

export const nachfassAPI = {
  uebersicht: (params) => api.get('/nachfass/uebersicht', { params }),
  pausieren: (leadId, bis) => api.post(`/nachfass/${leadId}/pausieren`, { bis }),
  fortsetzen: (leadId) => api.post(`/nachfass/${leadId}/fortsetzen`),
};

export const setterAPI = {
  posteingang: () => api.get('/setter/posteingang'),
  kennzahlen: (tage = 30) => api.get('/setter/kennzahlen', { params: { tage } }),
  selbsttest: () => api.get('/setter/selbsttest'),
  konversation: (id) => api.get(`/setter/konversation/${id}`),
  // Phase 1: selbst gesendet, nur abhaken. Phase 2: ueber die Cloud API senden.
  erledigt: (aufgabe_id) => api.post('/setter/erledigt', { aufgabe_id }),
  freigeben: (aufgabe_id) => api.post('/setter/freigeben', { aufgabe_id }),
  uebernehmen: (id) => api.post(`/setter/uebernehmen/${id}`),
};

// Kurzlinks (/r/<code>) fuer Flyer, QR-Codes und Aushaenge
export const kurzlinksAPI = {
  liste: () => api.get('/kurzlinks'),
  anlegen: (data) => api.post('/kurzlinks', data),
  aendern: (code, data) => api.patch(`/kurzlinks/${code}`, data),
  loeschen: (code) => api.delete(`/kurzlinks/${code}`),
  // Als Blob ueber die API holen, nicht als <a href>: der Endpunkt verlangt
  // Anmeldung, und ein einfacher Link traegt das Token nicht mit.
  qr: (code) => api.get(`/kurzlinks/${code}/qr`, { responseType: 'blob' }),
};

export default api;

// ── Erweiterung v6 „Heute & Auto-Pflege" (18.09.2026) ──────────────────────
export const aufgabenAPI = {
  heute: (alle) => api.get('/aufgaben/heute', { params: alle ? { alle: 1 } : {} }),
  liste: (params) => api.get('/aufgaben', { params }),
  anlegen: (daten) => api.post('/aufgaben', daten),
  aendern: (id, daten) => api.patch(`/aufgaben/${id}`, daten),
  erledigt: (id, notiz) => ern().post(ernPfad(`/aufgaben/${id}/erledigt`), notiz ? { notiz } : {}),
  verwerfen: (id, grund) => api.post(`/aufgaben/${id}/verwerfen`, { grund }),
  zaehler: () => api.get('/aufgaben/zaehler'),
  postenHaken: (id, nr, erledigt) => api.patch(`/aufgaben/${id}/posten/${nr}`, { erledigt }),
  delegieren: (ids, owner) => api.post('/aufgaben/delegieren', { ids, owner: owner || null }),
  verwerfenMehrere: (ids, grund) => api.post('/aufgaben/verwerfen-mehrere', { ids, grund }),
  zustaendigkeiten: () => api.get('/aufgaben/zustaendigkeiten'),
  zustaendigkeitSetzen: (bereich, user_id) => api.put('/aufgaben/zustaendigkeiten', { bereich, user_id: user_id || null }),
};
export const datenabgleichAPI = {
  offen: () => api.get('/datenabgleich/offen'),
  jeLead: (leadId) => api.get(`/datenabgleich/lead/${leadId}`),
  uebernehmen: (id, felder) => api.post(`/datenabgleich/${id}/uebernehmen`, { felder }),
  verwerfen: (id) => api.post(`/datenabgleich/${id}/verwerfen`),
};
export const kpiAPI = {
  heute: () => api.get('/kpi/heute'),
  ziele: () => api.get('/kpi/ziele'),
  zieleSetzen: (daten) => api.patch('/kpi/ziele', daten),
  zeitreihe: (monate) => api.get('/kpi/zeitreihe', { params: { monate } }),
  kursplanHeute: () => api.get('/kursplan/heute'),
  cashflow: () => api.get('/cashflow'),
  kursAendern: (id, daten) => api.put(`/kursplan/admin/courses/${id}`, daten),
  kursAusfall: (id, daten) => api.post(`/kursplan/admin/courses/${id}/ausfall`, daten),
  kursAusfallAufheben: (id, datum) => api.delete(`/kursplan/admin/courses/${id}/ausfall/${datum}`),
  arbeit: (userId, tage) => api.get('/team/arbeit', { params: { ...(userId ? { user_id: userId } : {}), tage } }),
};
export const onboardingAPI = {
  board: (tage) => api.get('/onboarding/board', { params: tage ? { tage } : {} }),
  stand: (leadId) => api.get(`/onboarding/${leadId}`),
  vertragAbgeschlossen: (leadId, daten) => api.post(`/leads/${leadId}/vertrag-abgeschlossen`, daten),
};
export const autopflegeAPI = {
  stand: (leadId) => api.get(`/leads/${leadId}/autopflege`),
  rueckgaengig: (leadId, ereignisId) => api.post(`/leads/${leadId}/autopflege/${ereignisId}/rueckgaengig`),
};
export const sprachnotizAPI = {
  // multipart: Audio-Blob oder Text — das Audio wird serverseitig nie gespeichert
  senden: (leadId, { audio, text }) => {
    const fd = new FormData();
    if (audio) fd.append('audio', audio, audio.name || 'notiz.webm');
    if (text) fd.append('text', text);
    return api.post(`/leads/${leadId}/sprachnotiz`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 });
  },
};
// Kurz-Feedback nach Beratung/Abschluss (31.10.2026): sprechen oder tippen, wahlweise „Nachfassen am“ (JJJJ-MM-TT)
export const kurzfeedbackAPI = {
  senden: (leadId, { audio, text, nachfassen }) => {
    const fd = new FormData();
    if (audio) fd.append('audio', audio, audio.name || 'feedback.webm');
    if (text) fd.append('text', text);
    if (nachfassen) fd.append('nachfassen', nachfassen);
    return api.post(`/leads/${leadId}/kurzfeedback`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 });
  },
  liste: (leadId) => api.get(`/leads/${leadId}/kurzfeedback`),
};
// Unterschrift-Station (03.10.2026): Vertrag am iMac senden, am Theken-iPad oder per QR unterschreiben
// Geschwister in einem Vertrag (26.10.2026): Familie laden, Vorschau, PDF; abgeschlossen wird an der Station (Art sammelvertrag)
export const sammelvertragAPI = {
  familie: (leadId) => api.get(`/sammelvertraege/familie/${leadId}`),
  vorschau: (entwurf) => api.post('/sammelvertraege/vorschau', entwurf),
  pdf: (id) => api.get(`/sammelvertraege/${id}/pdf`, { responseType: 'blob' }),
};

export const unterschriftAPI = {
  senden: (entwurf, art = 'vertrag') => api.post('/unterschrift/vorgaenge', { art, entwurf }),
  // Upsell als Link zum Versenden (22.10.2026): 48 h, Widerrufsbelehrung, das Team schickt den Link selbst
  versenden: (entwurf, art = 'zusatzvereinbarung') => api.post('/unterschrift/vorgaenge', { art, entwurf, versand: true }),
  links: (leadId) => api.get('/unterschrift/links', { params: { lead_id: leadId } }),
  // Pop-up „Upsell unterschrieben“ (26.10.2026): versendete Upsells, unterschrieben nach dem Zeiger
  unterschrieben: (seit) => api.get('/unterschrift/links/unterschrieben', { params: seit ? { seit } : {} }),
  status: (id) => api.get(`/unterschrift/vorgaenge/${id}`),
  abbrechen: (id, grund) => api.post(`/unterschrift/vorgaenge/${id}/abbrechen`, grund ? { grund } : {}),
  // Beim Neuladen / Schliessen des Tabs: ueberlebt das Entladen der Seite (keepalive). Ohne Antwort.
  abbrechenBeimVerlassen: (id) => {
    try {
      const token = tokenStore.getToken();
      fetch(`${API}/unterschrift/vorgaenge/${id}/abbrechen`, {
        method: 'POST', keepalive: true, credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ grund: 'Seite am iMac verlassen' }),
      }).catch(() => {});
    } catch { /* nichts zu tun */ }
  },
};

// Trainingsplaner A5 (02.10.2026): Leseuebersicht fuer /planer
export const trainingsplanerAPI = {
  // Übungsbibliothek (13.10.2026): Anleitung, Video, Muskeln, Gerät je Übungsname
  bibliothek: () => api.get('/trainingsplaner/bibliothek'),
  bibliothekSetzen: (body) => api.put('/trainingsplaner/bibliothek', body),
  // mitArchiv: auch archivierte Zyklen (zum Umbenennen)
  uebersicht: (mitArchiv = false) => api.get('/trainingsplaner/uebersicht', { params: mitArchiv ? { archiv: true } : undefined }),
  zyklus: (id) => api.get(`/trainingsplaner/zyklen/${id}`),
  // Umbenennen geht als einzige Änderung auch bei archivierten Zyklen
  name: (id, version, name) => api.patch(`/trainingsplaner/zyklen/${id}/name`, { version, name }),
  abdeckung: (id) => api.get(`/trainingsplaner/zyklen/${id}/abdeckung`),
  // Personen am Zyklus: Kader (Wettkampf) bzw. „Für wen ist dieser Plan?“ (Fitness, eigener Plan für PT-Kunden)
  athleten: (id, version, athleten_ids) => api.patch(`/trainingsplaner/zyklen/${id}/athleten`, { version, athleten_ids }),
};

// Ernährung (10.10.2026): Angaben, Wochenplan, Tausch, Fortschritt, Rezepte
const tresorPfad = (leadId) => (leadId ? `/ernaehrung/mitglied/${leadId}/tresor` : '/ernaehrung/tresor');
export const ernaehrungAPI = {
  optionen: () => ern().get(ernPfad('/ernaehrung/optionen')),
  uebersicht: () => ern().get(ernPfad('/ernaehrung/uebersicht')),
  mitglied: (id) => ern().get(ernPfad(`/ernaehrung/mitglied/${id}`)),
  profilSpeichern: (id, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${id}/profil`), daten),
  planErzeugen: (id, start, tage, vorlageId) => ern().post(ernPfad(`/ernaehrung/mitglied/${id}/plan`), { start, tage, vorlage_id: vorlageId || null }),
  alternativen: (id, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${id}/alternativen`), { params }),
  tauschen: (id, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${id}/tausch`), daten),
  einsetzen: (id, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${id}/einsetzen`), daten),
  gewicht: (id, kg, datum) => ern().post(ernPfad(`/ernaehrung/mitglied/${id}/gewicht`), { kg, datum }),
  rezepte: () => ern().get(ernPfad('/ernaehrung/rezepte')),
  rezeptPortion: (id, kcal) => ern().get(ernPfad(`/ernaehrung/rezepte/${id}/portion`), { params: kcal ? { kcal } : {} }),
  rezeptNeu: (daten) => ern().post(ernPfad('/ernaehrung/rezepte'), daten),
  rezeptAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/rezepte/${id}`), daten),
  // E3 Kochbücher (15.10.2026): Abschnitte aus Studio-Rezepten, Sichtbarkeit, Makro-Skalierung, Titelbild wie Rezeptfotos
  kochbuecher: () => ern().get(ernPfad('/ernaehrung/kochbuecher')),
  kochbuch: (id) => ern().get(ernPfad(`/ernaehrung/kochbuecher/${id}`)),
  kochbuchNeu: (daten) => ern().post(ernPfad('/ernaehrung/kochbuecher'), daten),
  kochbuchAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/kochbuecher/${id}`), daten),
  kochbuchLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/kochbuecher/${id}`)),
  kochbuchCover: (id, datei) => { const fd = new FormData(); fd.append('datei', datei, datei.name || 'cover.jpg');
    return ern().post(ernPfad(`/ernaehrung/kochbuecher/${id}/cover`), fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }); },
  kochbuchCoverWeg: (id) => ern().delete(ernPfad(`/ernaehrung/kochbuecher/${id}/cover`)),
  lebensmittelNeu: (daten) => ern().post(ernPfad('/ernaehrung/lebensmittel'), daten),
  // Richtpreise (11.10.2026): preis_kg in Euro je kg, null = unbekannt (Preis wird entfernt)
  lebensmittelAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/lebensmittel/${id}`), daten),
  einkaufsliste: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/einkaufsliste`), { params }),
  startbestand: () => ern().post(ernPfad('/ernaehrung/startbestand')),
  // Ernährungscoaching zubuchbar wie PT (10.10.2026)
  coachings: () => ern().get(ernPfad('/ernaehrung/coachings')),
  coachingUebernehmen: (id, daten) => ern().post(ernPfad(`/ernaehrung/coachings/${id}/uebernehmen`), daten),
  coachingFreischalten: (daten) => ern().post(ernPfad('/ernaehrung/coachings/freischalten'), daten),
  coachingStornieren: (id, grund) => ern().post(ernPfad(`/ernaehrung/coachings/${id}/stornieren`), { grund }),
  // Zusatz-Abo Ernährungscoaching (E0, 15.10.2026): Position auf der Monatsrechnung, Einzug mit dem Mandat
  abos: () => ern().get(ernPfad('/ernaehrung/abos')),
  aboAnlegen: (daten) => ern().post(ernPfad('/ernaehrung/abos'), daten),
  aboKuendigen: (id, daten) => ern().post(ernPfad(`/ernaehrung/abos/${id}/kuendigen`), daten),
  aboPausieren: (id, grund) => ern().post(ernPfad(`/ernaehrung/abos/${id}/pausieren`), { grund }),
  aboFortsetzen: (id) => ern().post(ernPfad(`/ernaehrung/abos/${id}/fortsetzen`)),
  aboProdukt: () => ern().get(ernPfad('/ernaehrung/abo-produkt')),
  aboProduktSetzen: (daten) => ern().put(ernPfad('/ernaehrung/abo-produkt'), daten),
  // E23 Neuer Kunde in einem Fluss (15.10.2026): Entwurf → Freigabe („Abschlüsse“) → nur die Leitung bestätigt
  abschlussOptionen: () => ern().get(ernPfad('/ernaehrung/abschluss/optionen')),
  abschluesse: (params) => ern().get(ernPfad('/ernaehrung/abschluss'), { params }),
  abschluss: (id) => ern().get(ernPfad(`/ernaehrung/abschluss/${id}`)),
  abschlussVorschau: (daten) => ern().post(ernPfad('/ernaehrung/abschluss/vorschau'), daten),
  abschlussAnlegen: (daten) => ern().post(ernPfad('/ernaehrung/abschluss'), daten),
  abschlussBestaetigen: (id) => ern().post(ernPfad(`/ernaehrung/abschluss/${id}/bestaetigen`)),
  abschlussVerwerfen: (id, grund) => ern().post(ernPfad(`/ernaehrung/abschluss/${id}/verwerfen`), { grund: grund || '' }),
  // E19 (15.10.2026): Einwilligung Gesundheitsdaten — Log am Mitglied, Eintragen/Widerruf, Freigabe des Textes
  einwilligung: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/einwilligung`)),
  einwilligungErteilen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/einwilligung`), daten),
  einwilligungWiderrufen: (leadId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/einwilligung`)),
  einwilligungEinstellung: () => ern().get(ernPfad('/ernaehrung/einwilligung-einstellung')),
  einwilligungFreigeben: (daten) => ern().put(ernPfad('/ernaehrung/einwilligung-freigabe'), daten),
  // E10 KI-Wochenauswertung (15.10.2026): Zusammenfassung, Entwurf, Plan-Vorschlag je Check-in — nichts geht automatisch raus
  kiAuswertungStatus: () => ern().get(ernPfad('/ernaehrung/ki-auswertungen/status')),
  kiAuswertungenOffen: () => ern().get(ernPfad('/ernaehrung/ki-auswertungen')),
  kiAuswertung: (leadId, woche) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/ki-auswertung`), { params: woche ? { woche } : {} }),
  kiAuswertungAnstossen: (leadId) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/ki-auswertung`)),
  kiPlanVorschau: (id) => ern().get(ernPfad(`/ernaehrung/ki-auswertungen/${id}/plan-vorschau`)),
  kiPlanUebernehmen: (id) => ern().post(ernPfad(`/ernaehrung/ki-auswertungen/${id}/plan-uebernehmen`)),
  kiAuswertungVerwerfen: (id) => ern().post(ernPfad(`/ernaehrung/ki-auswertungen/${id}/verwerfen`)),
  tagsSetzen: (leadId, tags) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/tags`), { tags }),
  // E1 Ziele & Zugangsstufen (15.10.2026)
  ziele: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/ziele`)),
  zieleSpeichern: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/ziele`), daten),
  // E18 Wettkampf-Modus (Kader)
  wettkampf: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/wettkampf`)),
  wettkampfAnlegen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/wettkampf`), daten),
  wettkampfAendern: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/wettkampf`), daten),
  wettkampfPlanFreigeben: (leadId) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/wettkampf/plan-freigeben`)),
  wettkampfPhasenplan: (leadId, phase) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/wettkampf/phasen/${phase}/plan`)),
  wettkampfBeenden: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/wettkampf/beenden`), daten),
  // E9 Apple Health (15.10.2026): Schritte je Tag mit Quelle, Ziel, Trend + Einwilligungsstand; Widerruf vom Team
  schritte: (leadId, tage = 28) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/schritte`), { params: { tage } }),
  healthWiderrufen: (leadId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/health/einwilligung`)),
  // E21 Garmin (15.10.2026): Karte „Erholung“ (Kunde › Entwicklung), Widerruf eintragen, Schwellen der Signale (Leitung)
  wearable: (leadId, tage = 28) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/wearable`), { params: { tage } }),
  wearableWiderrufen: (leadId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/wearable`)),
  wearableSchwellen: () => ern().get(ernPfad('/ernaehrung/wearable/schwellen')),
  wearableSchwellenSetzen: (daten) => ern().put(ernPfad('/ernaehrung/wearable/schwellen'), daten),
  // E22 Zyklus (15.10.2026): Karte nur für Leitung und Coach des Abos (sonst 403); Widerruf löscht die Zyklusdaten sofort
  zyklus: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/zyklus`)),
  zyklusEinstellung: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/zyklus/einstellung`), daten),
  zyklusWiderrufen: (leadId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/zyklus/einwilligung`)),
  // E2 (15.10.2026): Kommentar des Coachs zum Tag
  tageskommentar: (leadId, datum, text) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/tagebuch/${datum}/kommentar`), { text }),
  // E17 (15.10.2026): Kurs-Ernährungs-Tipps
  kurstipps: () => ern().get(ernPfad('/ernaehrung/kurstipps')),
  kurstippNeu: (daten) => ern().post(ernPfad('/ernaehrung/kurstipps'), daten),
  kurstippAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/kurstipps/${id}`), daten),
  kurstippLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/kurstipps/${id}`)),
  kurstippsModus: (modus) => ern().put(ernPfad('/ernaehrung/kurstipps/einstellung'), { modus }),
  // Rezeptvorschläge per KI (eigener Schalter KI_REZEPTE, keine Personendaten)
  vorschlaegeStatus: () => ern().get(ernPfad('/ernaehrung/vorschlaege/status')),
  vorschlaege: (daten) => ern().post(ernPfad('/ernaehrung/vorschlaege'), daten, { timeout: 180000 }),
  rezeptUebernehmen: (id) => ern().post(ernPfad(`/ernaehrung/rezepte/${id}/uebernehmen`)),
  // Open Food Facts (ODbL): Vorschau je Barcode, Übernehmen mit bestätigten Merkmalen
  offVorschau: (barcode) => ern().get(ernPfad(`/ernaehrung/lebensmittel/openfoodfacts/${encodeURIComponent(barcode)}`), { timeout: 30000 }),
  offUebernehmen: (daten) => ern().post(ernPfad('/ernaehrung/lebensmittel/openfoodfacts'), daten, { timeout: 30000 }),
  // Bundeslebensmittelschlüssel (BLS 4.0, CC BY 4.0): Katalog durchsuchen, einzelne Lebensmittel übernehmen
  blsSuche: (q) => ern().get(ernPfad('/ernaehrung/bls'), { params: { q } }),
  blsUebernehmen: (daten) => ern().post(ernPfad('/ernaehrung/lebensmittel/bls'), daten),
  vorschlagVerwerfen: (id) => ern().delete(ernPfad(`/ernaehrung/rezepte/${id}`)),
  // Plan-Vorlagen: Rezept je Wochentag und Mahlzeit, beim Zuweisen aufs Tagesziel skaliert
  vorlagen: () => ern().get(ernPfad('/ernaehrung/vorlagen')),
  vorlage: (id) => ern().get(ernPfad(`/ernaehrung/vorlagen/${id}`)),
  vorlageNeu: (daten) => ern().post(ernPfad('/ernaehrung/vorlagen'), daten),
  vorlageAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/vorlagen/${id}`), daten),
  vorlageDuplizieren: (id) => ern().post(ernPfad(`/ernaehrung/vorlagen/${id}/duplizieren`)),
  vorlageLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/vorlagen/${id}`)),
  vorlageAuto: (daten) => ern().post(ernPfad('/ernaehrung/vorlagen/auto'), daten),
  vorlageAusPlan: (daten) => ern().post(ernPfad('/ernaehrung/vorlagen/aus-plan'), daten),
  // E4 Plan-Builder (15.10.2026): Pläne je Mitglied, Setup-Empfehlung, Editor mit `version`, gespeicherte Splits
  plaene: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/plaene`)),
  planEmpfehlung: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/plan-empfehlung`), { params }),
  planMitSetup: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/plan`), daten, { timeout: 120000 }),
  planLesen: (leadId, planId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/plan/${planId}`)),
  planStatus: (leadId, planId, status) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/plan/${planId}/status`), { status }),
  planLoeschen: (leadId, planId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/plan/${planId}`)),
  zutatAendern: (leadId, planId, datum, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/plan/${planId}/tag/${datum}/zutat`), daten),
  mahlzeitVerschieben: (leadId, planId, datum, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/plan/${planId}/tag/${datum}/verschieben`), daten),
  splits: () => ern().get(ernPfad('/ernaehrung/splits')),
  splitSpeichern: (daten) => ern().post(ernPfad('/ernaehrung/splits'), daten),
  splitLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/splits/${id}`)),
  // Rezeptfotos (öffentlich ausgeliefert unter bild_url) und Personen je Mahlzeit
  rezeptBild: (id, datei) => { const fd = new FormData(); fd.append('datei', datei, datei.name || 'foto.jpg');
    return ern().post(ernPfad(`/ernaehrung/rezepte/${id}/bild`), fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }); },
  rezeptBildWeg: (id) => ern().delete(ernPfad(`/ernaehrung/rezepte/${id}/bild`)),
  personen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/personen`), daten),
  tagebuch: (leadId, datum) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/tagebuch`), { params: { datum } }),
  dashboard: () => ern().get(ernPfad('/ernaehrung/dashboard')),
  planPdf: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/plan.pdf`), { params, responseType: 'blob', timeout: 60000 }),
  // Formulare und Check-ins
  formulare: () => ern().get(ernPfad('/ernaehrung/formulare')),
  formularNeu: (daten) => ern().post(ernPfad('/ernaehrung/formulare'), daten),
  formularAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/formulare/${id}`), daten),
  formularLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/formulare/${id}`)),
  formularZuweisen: (id, leadIds, faellig) => ern().post(ernPfad(`/ernaehrung/formulare/${id}/zuweisen`), { lead_ids: leadIds, faellig_am: faellig || null }),
  checkinEinstellung: (daten) => ern().put(ernPfad('/ernaehrung/formulare/einstellung'), daten),
  formulareEingang: () => ern().get(ernPfad('/ernaehrung/formulare/eingang')),
  mitgliedFormulare: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/formulare`)),
  coachAntwort: (zid, text) => ern().post(ernPfad(`/ernaehrung/zuweisungen/${zid}/antwort`), { text }),
  zuweisungGelesen: (zid, gelesen = true) => ern().post(ernPfad(`/ernaehrung/zuweisungen/${zid}/gelesen`), { gelesen }),
  // E5 (15.10.2026): Einstellungen je Kunde (eigenes Check-in-Formular, Zeitplan, Erinnerung, ausgeblendete Felder),
  // Auswertung für Kunde › Entwicklung, Körperdaten von Hand
  formularKunde: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/formular-einstellung`), daten),
  auswertung: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/auswertung`)),
  koerperdaten: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperdaten`)),
  koerperdatenEintragen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperdaten`), daten),
  koerperdatenLoeschen: (leadId, kid) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperdaten/${kid}`)),
  // Körperanalyse (InBody, 11.10.2026): Schalter (nur Leitung), Messung von Hand, Löschen, Widerruf eintragen.
  // Die Karte selbst kommt mit mitglied(id) (Feld koerperanalyse).
  koerperEinstellung: () => ern().get(ernPfad('/ernaehrung/koerperanalyse/einstellung')),
  koerperEinstellungSetzen: (daten) => ern().put(ernPfad('/ernaehrung/koerperanalyse/einstellung'), daten),
  messungEintragen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperanalyse`), daten),
  koerperBogen: (leadId, id) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperanalyse/${id}/bogen.pdf`), { responseType: 'blob', timeout: 60000 }),
  koerperBogenTresor: (leadId, id) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperanalyse/${id}/tresor`)),
  messungLoeschen: (leadId, messungId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperanalyse/${messungId}`)),
  koerperWiderruf: (leadId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/koerperanalyse/einwilligung`)),
  // Waagenstation (29.10.2026): Gastmessungen der InBody über den Raspberry Pi, Seite /waage
  stationEingang: () => api.get('/ernaehrung/koerperanalyse/station/eingang'),
  stationZuordnen: (id, daten) => api.post(`/ernaehrung/koerperanalyse/station/eingang/${id}/zuordnen`, daten),
  stationVerwerfen: (id) => api.post(`/ernaehrung/koerperanalyse/station/eingang/${id}/verwerfen`),
  stationCode: (code) => api.post('/ernaehrung/koerperanalyse/station/code', { code }),
  stationRueckgaengig: (id) => api.post(`/ernaehrung/koerperanalyse/station/eingang/${id}/rueckgaengig`),
  stationAutomatik: (aktiv) => api.put('/ernaehrung/koerperanalyse/station/automatik', { aktiv }),
  stationExport: (von, bis) => api.get('/ernaehrung/koerperanalyse/station/export', { params: { von, bis }, responseType: 'blob' }),
  // Genuss-Planer (11.10.2026, Stufe 1): Schalter (nur Leitung), Wochenkarte, Genussmahlzeiten, Anzahl je Woche.
  // Gerechnet wird nur auf dem Server — die Vorschau speichert nichts.
  funktionen: () => ern().get(ernPfad('/ernaehrung/funktionen')),
  funktionenSetzen: (daten) => ern().put(ernPfad('/ernaehrung/funktionen'), daten),
  genussVorlagen: () => ern().get(ernPfad('/ernaehrung/genuss/vorlagen')),
  genussWoche: (leadId, datum) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/woche`), { params: datum ? { datum } : {} }),
  genussVorschau: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/genuss/vorschau`), daten),
  genussAnlegen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/genuss`), daten),
  genussLoeschen: (leadId, id) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/genuss/${id}`)),
  genussGrenze: (leadId, n) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/genuss-grenze`), { max_je_woche: n }),
  // Kommentare zu Tagebuch-Einträgen (11.10.2026, Stufe 2): Der Verlauf kommt mit `tagebuch` (je Eintrag `kommentare`).
  kommentar: (leadId, eintragId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/tagebuch/${eintragId}/kommentar`), daten),
  kommentareGelesen: (leadId, eintragId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/tagebuch/${eintragId}/kommentare/gelesen`), daten),
  kommentareOffen: () => ern().get(ernPfad('/ernaehrung/kommentare/offen')),
  // Essensfoto (11.10.2026, Stufe 3): Das Bild kommt nur mit Anmeldung (als Blob, nie über eine öffentliche Adresse);
  // Korrektur der Nährwerte eines Foto-Eintrags; Widerruf der Einwilligung vom Team eingetragen.
  essensfoto: (leadId, fotoId, klein) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/foto/${fotoId}`), { params: klein ? { klein: 1 } : {}, responseType: 'blob', timeout: 30000 }),
  fotoNaehrwerte: (leadId, eintragId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/tagebuch/${eintragId}/naehrwerte`), daten),
  fotoWiderruf: (leadId) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/foto/einwilligung`)),
  // Tresor (E8, 15.10.2026): Material vom Coach — je Mitglied (leadId) oder für alle Coaching-Kunden (leadId null).
  // Dateien kommen nur mit Anmeldung als Blob, nie über eine öffentliche Adresse.
  tresor: (leadId) => ern().get(ernPfad(tresorPfad(leadId))),
  tresorHochladen: (leadId, datei, felder) => { const fd = new FormData(); fd.append('datei', datei, datei.name || 'datei');
    Object.entries(felder).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') fd.append(k, v); });
    return ern().post(ernPfad(tresorPfad(leadId)), fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }); },
  tresorLink: (leadId, daten) => ern().post(ernPfad(`${tresorPfad(leadId)}/link`), daten),
  tresorAendern: (leadId, id, daten) => ern().patch(ernPfad(`${tresorPfad(leadId)}/${id}`), daten),
  tresorLoeschen: (leadId, id) => ern().delete(ernPfad(`${tresorPfad(leadId)}/${id}`)),
  tresorDatei: (leadId, id) => ern().get(ernPfad(`${tresorPfad(leadId)}/${id}/datei`), { responseType: 'blob', timeout: 120000 }),
  // 3-Monats-Erfolgsreport (E15, 15.10.2026): Entwurf → Erfolge/Empfehlung/Foto-Haken → Freigabe legt das PDF in den Tresor.
  // Das PDF kommt nur mit Anmeldung als Blob.
  erfolgsreports: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/erfolgsreports`)),
  erfolgsreportAnlegen: (leadId) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/erfolgsreports`)),
  erfolgsreportAendern: (leadId, rid, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/erfolgsreports/${rid}`), daten),
  erfolgsreportFreigeben: (leadId, rid) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/erfolgsreports/${rid}/freigeben`)),
  erfolgsreportPdf: (leadId, rid) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/erfolgsreports/${rid}/pdf`), { responseType: 'blob', timeout: 120000 }),
  // Automatik-Sequenzen (E7, 15.10.2026): Sequenz = Liste von Schritten; Läufe je Mitglied; Starten/Stoppen von Hand.
  automatikSequenzen: () => ern().get(ernPfad('/ernaehrung/automatik/sequenzen')),
  automatikSequenz: (id) => ern().get(ernPfad(`/ernaehrung/automatik/sequenzen/${id}`)),
  automatikSequenzNeu: (daten) => ern().post(ernPfad('/ernaehrung/automatik/sequenzen'), daten),
  automatikSequenzAendern: (id, daten) => ern().put(ernPfad(`/ernaehrung/automatik/sequenzen/${id}`), daten),
  automatikSequenzLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/automatik/sequenzen/${id}`)),
  automatikStarten: (id, leadId) => ern().post(ernPfad(`/ernaehrung/automatik/sequenzen/${id}/starten`), { lead_id: leadId }),
  automatikLaeufe: (nurLaufend) => ern().get(ernPfad('/ernaehrung/automatik/laeufe'), { params: nurLaufend ? { nur_laufend: true } : {} }),
  automatikMitglied: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/automatik`)),
  automatikStoppen: (laufId, grund) => ern().post(ernPfad(`/ernaehrung/automatik/laeufe/${laufId}/stoppen`), { grund: grund || null }),
  // E6 Coach-Cockpit (15.10.2026): Kundenliste vorgerechnet, Konversation = Coach-Chat + Karten, Kundenakte-Panels.
  // Sprachnotiz als multipart (Transkript über den eigenen Whisper-Dienst); Aufnahme nur als Blob mit Anmeldung.
  cockpit: (params) => ern().get(ernPfad('/ernaehrung/cockpit'), { params }),
  cockpitUebersicht: (leadId) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/uebersicht`)),
  cockpitKonversation: (leadId) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/konversation`)),
  cockpitNachricht: (leadId, { text, antwortAuf, audio }) => { const fd = new FormData(); fd.append('text', text || ''); fd.append('antwort_auf', antwortAuf || '');
    if (audio) fd.append('audio', audio, audio.name || 'sprachnotiz.webm');
    return ern().post(ernPfad(`/ernaehrung/cockpit/${leadId}/nachricht`), fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 }); },
  cockpitNachrichtBearbeiten: (leadId, nid, text) => ern().patch(ernPfad(`/ernaehrung/cockpit/${leadId}/nachricht/${nid}`), { text }),
  cockpitReaktion: (leadId, nid, emoji) => ern().post(ernPfad(`/ernaehrung/cockpit/${leadId}/nachricht/${nid}/reaktion`), { emoji }),
  cockpitAudio: (leadId, nid) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/nachricht/${nid}/audio`), { responseType: 'blob', timeout: 60000 }),
  cockpitGelesen: (leadId) => ern().post(ernPfad(`/ernaehrung/cockpit/${leadId}/gelesen`)),
  cockpitUngelesen: (leadId) => ern().post(ernPfad(`/ernaehrung/cockpit/${leadId}/ungelesen`)),
  cockpitVideos: (leadId, erlaubt) => ern().put(ernPfad(`/ernaehrung/cockpit/${leadId}/videos`), { erlaubt }),
  cockpitErnaehrung: (leadId) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/ernaehrung`)),
  cockpitPlanStatus: (leadId, planId, status) => ern().put(ernPfad(`/ernaehrung/cockpit/${leadId}/plan/${planId}/status`), { status }),
  cockpitPlanLoeschen: (leadId, planId) => ern().delete(ernPfad(`/ernaehrung/cockpit/${leadId}/plan/${planId}`)),
  cockpitZahlungen: (leadId) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/zahlungen`)),
  cockpitAutomatik: (leadId) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/automatik`)),
  cockpitAufgaben: (leadId) => ern().get(ernPfad(`/ernaehrung/cockpit/${leadId}/aufgaben`)),
  cockpitAufgabeNeu: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/cockpit/${leadId}/aufgaben`), daten),
  // E6a Kunde › Entwicklung: alles vorgerechnet je Zeitraum; Messwert ändern/löschen (nie Start-/Zielgewicht)
  entwicklung: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/entwicklung`), { params }),
  messwertAendern: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/messwert`), daten),
  messwertLoeschen: (leadId, params) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/messwert`), { params }),
  fortschrittsfoto: (leadId, fid, klein) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/fortschrittsfoto/${fid}`), { params: klein ? { klein: 1 } : {}, responseType: 'blob', timeout: 30000 }),
  // E6b Volles Ernährungsprotokoll: Woche + Tag, „Freundlich erinnern“, Schnellvorlagen für den Tageskommentar
  protokoll: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/protokoll`), { params }),
  erinnern: (leadId, datum) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/protokoll/${datum}/erinnern`)),
  kommentarVorlagen: () => ern().get(ernPfad('/ernaehrung/kommentar-vorlagen')),
  kommentarVorlageNeu: (text) => ern().post(ernPfad('/ernaehrung/kommentar-vorlagen'), { text }),
  kommentarVorlagenStart: () => ern().post(ernPfad('/ernaehrung/kommentar-vorlagen/start')),
  kommentarVorlageLoeschen: (id) => ern().delete(ernPfad(`/ernaehrung/kommentar-vorlagen/${id}`)),
  // E12 Risiko-Ampel: Übersicht, Gewichte (nur Leitung), Lauf von Hand
  risiko: () => ern().get(ernPfad('/ernaehrung/risiko')),
  risikoGewichte: (daten) => ern().put(ernPfad('/ernaehrung/risiko/gewichte'), daten),
  risikoLauf: () => ern().post(ernPfad('/ernaehrung/risiko/lauf'), null, { timeout: 120000 }),
  // W2.2 Coach-Cockpit: Status-Schwellen, Momentum-Gewichte, Glückwunsch-Schalter (nur Leitung)
  risikoStatusEinstellungen: () => ern().get(ernPfad('/ernaehrung/risiko/status-einstellungen')),
  risikoStatusEinstellungenSetzen: (daten) => ern().put(ernPfad('/ernaehrung/risiko/status-einstellungen'), daten),
  mitgliedRisiko: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/risiko`)),
  // E20 Habit-Tracker + Tagesstatus: Karte Gewohnheiten (Kunde › Entwicklung), Bibliothek, Tagesstatus nachtragen
  gewohnheiten: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/gewohnheiten`), { params }),
  gewohnheitZuweisen: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/gewohnheiten`), daten),
  gewohnheitBeenden: (leadId, gid) => ern().delete(ernPfad(`/ernaehrung/mitglied/${leadId}/gewohnheiten/${gid}`)),
  gewohnheitVorlagen: () => ern().get(ernPfad('/ernaehrung/gewohnheiten/vorlagen')),
  gewohnheitVorlageNeu: (daten) => ern().post(ernPfad('/ernaehrung/gewohnheiten/vorlagen'), daten),
  gewohnheitVorlageAendern: (vid, daten) => ern().put(ernPfad(`/ernaehrung/gewohnheiten/vorlagen/${vid}`), daten),
  gewohnheitVorlageLoeschen: (vid) => ern().delete(ernPfad(`/ernaehrung/gewohnheiten/vorlagen/${vid}`)),
  tagesstatus: (leadId, params) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/tagesstatus`), { params }),
  tagesstatusSetzen: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/tagesstatus`), daten),
};
// W3.2 Ernährungsplan mit Varianten (29.10.2026, backend/ernaehrung/router_varianten.py): gleicher Client wie ernaehrungAPI,
// läuft also auch auf /coach über den Spiegel. Jede Änderung schickt `version` mit und bekommt den ganzen Plan zurück.
const planPfad = (leadId, planId, rest = '') => ernPfad(`/ernaehrung/mitglied/${leadId}/plan/${planId}${rest}`);
export const ernaehrungVariantenAPI = {
  neu: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/plan-varianten`), daten, { timeout: 120000 }),
  steuerung: (leadId, planId, daten) => ern().put(planPfad(leadId, planId, '/steuerung'), daten),
  varianteNeu: (leadId, planId, daten) => ern().post(planPfad(leadId, planId, '/varianten'), daten),
  varianteAendern: (leadId, planId, vid, daten) => ern().put(planPfad(leadId, planId, `/varianten/${vid}`), daten, { timeout: 60000 }),
  varianteDuplizieren: (leadId, planId, vid, version) => ern().post(planPfad(leadId, planId, `/varianten/${vid}/duplizieren`), { version }),
  varianteLoeschen: (leadId, planId, vid, version) => ern().delete(planPfad(leadId, planId, `/varianten/${vid}`), { params: { version } }),
  slot: (leadId, planId, vid, slot, daten) => ern().put(planPfad(leadId, planId, `/varianten/${vid}/slot/${slot}`), daten),
  fuellen: (leadId, planId, daten) => ern().post(planPfad(leadId, planId, '/varianten-fuellen'), daten, { timeout: 120000 }),
  rezeptwahl: (leadId, planId) => ern().get(planPfad(leadId, planId, '/rezeptwahl')),
  alsVorlage: (leadId, planId, daten) => ern().post(planPfad(leadId, planId, '/als-vorlage'), daten),
  einkaufWoche: (leadId, woche) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/einkauf-woche`), { params: woche ? { woche } : {} }),
  einkaufWocheAbhaken: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/einkauf-woche/abhaken`), daten),
};
// bild_url kommt relativ vom Server (/api/ernaehrung/bilder/…)
export const bildSrc = (url) => {
  if (!url) return null;
  // Trainer-Webseite: geschützte Bilder, PDFs und Audio über den Spiegel mit kurzlebigem Token (die öffentlichen
  // Rezeptfotos unter /api/ernaehrung/bilder/ bleiben, wie sie sind)
  if (ernaehrungClient && ernaehrungPraefix && /^\/api\//.test(url) && !url.startsWith('/api/ernaehrung/bilder/')) {
    const basis = ernaehrungClient.defaults?.baseURL || '';
    const pfad = `${ernaehrungPraefix}${url.replace(/^\/api/, '')}`;
    const t = ernaehrungBildToken ? `${pfad.includes('?') ? '&' : '?'}t=${encodeURIComponent(ernaehrungBildToken)}` : '';
    return `${basis}${pfad}${t}`;
  }
  return `${API_URL || ''}${url}`;
};

// Kraftlog (10.10.2026): Plan gegen Ist je Mitglied, Coach-Kommentar
// Gewohnheiten (13.10.2026): Coach weist zu, das Mitglied hakt in der App ab
// W2.5 Check-ins (29.10.2026): Rhythmus, Urlaub, Einschieben, Startwerte, Fragebogen je Kunde — CRM und (über den
// Ernährungs-Client) /coach-Spiegel. Die Inbox auf /coach ruft der Trainer-Client direkt (pages/CoachCheckins.js).
export const checkinRhythmusAPI = {
  einstellung: (leadId) => ern().get(ernPfad(`/ernaehrung/mitglied/${leadId}/checkin-einstellung`)),
  speichern: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/checkin-einstellung`), daten),
  einschieben: (leadId, faellig) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/checkin-einschieben`), { faellig_am: faellig || null }),
  startwerte: (leadId, daten) => ern().post(ernPfad(`/ernaehrung/mitglied/${leadId}/startwerte`), daten),
  fragebogen: (leadId, daten) => ern().put(ernPfad(`/ernaehrung/mitglied/${leadId}/fragebogen`), daten),
};

export const gewohnheitenAPI = {
  stand: (memberId) => ern().get(ernPfad(`/gewohnheiten/mitglied/${memberId}`)),
  anlegen: (memberId, body) => ern().post(ernPfad(`/gewohnheiten/mitglied/${memberId}`), body),
  beenden: (id) => ern().delete(ernPfad(`/gewohnheiten/${id}`)),
  aktiv: (id, aktiv) => ern().patch(ernPfad(`/gewohnheiten/${id}/aktiv`), { aktiv }),   // W3.3 aktiv ja/nein
};

// Check-Up-Ablauf (19.10.2026): Ergebnisse aus der Trainer-App, im CRM nur lesen
export const checkupAblaufAPI = {
  mitglied: (leadId) => api.get(`/checkup-ablauf/mitglied/${leadId}`),
  // Stepper im CRM (Kundenakte Schritt 3+4, 28.10.2026): dieselben Schritte wie am Studio-Tablet, CRM-Anmeldung → Trainer-Profil
  ablauf: (id) => api.get(`/checkup-ablauf/${id}/ablauf`),
  einwilligung: (id, daten) => api.post(`/checkup-ablauf/${id}/einwilligung`, daten),
  messung: (id, code) => api.post(`/checkup-ablauf/${id}/messung`, { code }),
  messwerte: (id, daten) => api.put(`/checkup-ablauf/${id}/messwerte`, daten),
  antworten: (id, antworten) => api.put(`/checkup-ablauf/${id}/antworten`, { antworten }),
  kurse: (id, termine) => api.post(`/checkup-ablauf/${id}/kurse`, { termine }),
  befund: (id, daten) => api.put(`/checkup-ablauf/${id}/befund`, daten),
  empfehlung: (id) => api.post(`/checkup-ablauf/${id}/empfehlung`, {}),
  abschluss: (id, daten) => api.post(`/checkup-ablauf/${id}/abschluss`, daten, { timeout: 60000 }),
  art: (id, art) => api.put(`/checkup-ablauf/${id}/art`, { art }),        // Coach-Cockpit W1: erst|start|ergebnis|abschluss
  // Coach-Cockpit W1.6/W1.6b (w1-ergebnis): Ergebnis-/Abschluss-Check-up, Schritt 3
  ergebnis: (id) => api.get(`/checkup-ablauf/${id}/ergebnis`),
  einschaetzungVorschlag: (id) => api.post(`/checkup-ablauf/${id}/einschaetzung/vorschlag`, {}),
  einschaetzung: (id, daten) => api.put(`/checkup-ablauf/${id}/einschaetzung`, daten),
  weg: (id, daten) => api.put(`/checkup-ablauf/${id}/weg`, daten),
  foto: (id, fid) => api.get(`/checkup-ablauf/${id}/ergebnis/foto/${fid}`, { responseType: 'blob' }),
  pdf: (id) => api.get(`/checkup-ablauf/${id}/ergebnis/pdf`, { responseType: 'blob', timeout: 60000 }),
};
// Einrichtung Check-up (lesen leads.edit, schreiben Leitung) — w1-ergebnis: Einschätzung und Kurs-Zuordnung
export const checkupEinstellungenAPI = {
  lesen: () => api.get('/checkup-einstellungen'),
  setzen: (daten) => api.put('/checkup-einstellungen', daten),
};
// Coach-Cockpit W1 (29.10.2026): Angebotsvorlagen (lesen leads.edit, schreiben Leitung) und Beschleunigungsphase
export const angebotsvorlagenAPI = {
  liste: () => api.get('/angebotsvorlagen'),
  setzen: (schluessel, daten) => api.put(`/angebotsvorlagen/${schluessel}`, daten),
  neu: (daten) => api.post('/angebotsvorlagen', daten),
};
export const phasenAPI = {
  liste: (leadId) => api.get('/phasen', { params: { lead_id: leadId } }),
  anlegen: (daten) => api.post('/phasen', daten),
  ergaenzungMonat2: (phaseId) => api.post(`/phasen/${phaseId}/ergaenzung/monat2`, {}),
};

export const trainingslogAPI = {
  mitglied: (memberId) => api.get(`/trainingslog/mitglied/${memberId}`),
  kommentar: (id, text) => api.put(`/trainingslog/${id}/kommentar`, { text }),
  // Faden unter dem Kommentar (13.10.2026): das Mitglied antwortet in der App, das Team hier
  antwort: (id, text) => api.post(`/trainingslog/${id}/antwort`, { text }),
  gelesen: (id) => api.post(`/trainingslog/${id}/gelesen`),
  zyklus: (zid) => api.get(`/trainingslog/zyklus/${zid}`),
  // PT-Zugang zum Kraftlog: nur nach Check-Up mit PT-Buchung (das Team bestätigt die Buchung)
  ptStand: (memberId) => api.get(`/trainingslog/mitglied/${memberId}/pt-zugang`),
  ptFreischalten: (memberId) => api.post(`/trainingslog/mitglied/${memberId}/pt-zugang`),
  ptBeenden: (memberId) => api.delete(`/trainingslog/mitglied/${memberId}/pt-zugang`),
};

// Trainer-Webseite (10.10.2026): Zugänge für Overviewer und Personal Trainer
export const coachVerwaltungAPI = {
  liste: () => api.get('/coach-verwaltung'),
  anlegen: (daten) => api.post('/coach-verwaltung', daten),
  aendern: (id, daten) => api.patch(`/coach-verwaltung/${id}`, daten),
  einladen: (id) => api.post(`/coach-verwaltung/${id}/einladen`),
  abmelden: (id) => api.post(`/coach-verwaltung/${id}/abmelden`),
};

export const kursbestandAPI = {
  lesen: () => api.get('/kursbestand'),
  kurse: () => api.get('/kursplan/courses'),
  zuordnen: (memberId, courseIds) => api.put(`/kursbestand/${memberId}`, { course_ids: courseIds }),
  erstvorschlag: () => api.post('/kursbestand/erstvorschlag'),
  vorschlaege: (neu) => api.get('/kursbestand/vorschlaege', { params: neu ? { neu: 1 } : {} }),
  vorschlagEntscheiden: (id, annehmen) => api.post(`/kursbestand/vorschlaege/${id}`, { annehmen }),
  vorschlaegeSichere: () => api.post('/kursbestand/vorschlaege/sichere'),
};
/* Lehrplan (15.10.2026): Inhalte je Gürtel, Seite /lehrplan — Lesen kursplan.view, Ändern settings.edit_general */
/* Einsatzplanung Schritt 1 — Trainer-Profil (16.10.2026, backend/einsatz/) */
export const einsatzAPI = {
  trainer: () => api.get('/einsatz/trainer'),
  abgleich: () => api.get('/einsatz/trainer/abgleich'),
  /* Kalender-Abo der eigenen Einsätze fürs Handy (27.10.2026). basis = absolute API-Adresse, unter der das Handy das CRM erreicht */
  aboStand: () => api.get('/einsatz/kalender-abo'),
  aboErzeugen: () => api.post('/einsatz/kalender-abo', { basis: new URL(api.defaults.baseURL, window.location.origin).href }),
  aboLoeschen: () => api.delete('/einsatz/kalender-abo'),
  abgleichUebernehmen: (trainerId, auswahl) => api.post(`/einsatz/trainer/${trainerId}/abgleich`, auswahl),
  profil: (trainerId) => api.get(`/einsatz/trainer/${trainerId}`),
  profilSpeichern: (trainerId, daten) => api.put(`/einsatz/trainer/${trainerId}`, daten),
  qualifikation: (trainerId) => api.get(`/einsatz/trainer/${trainerId}/qualifikation`),
  qualifikationSetzen: (trainerId, qualifikation) => api.put(`/einsatz/trainer/${trainerId}/qualifikation`, { qualifikation }),
};
export const lehrplanAPI = {
  seite: () => api.get('/curriculum/seite'),
  anlegen: (daten) => api.post('/curriculum', daten),
  aendern: (id, daten) => api.put(`/curriculum/${id}`, daten),
  loeschen: (id) => api.delete(`/curriculum/${id}`),
  reihenfolge: (belt, ids) => api.post('/curriculum/reihenfolge', { belt, ids }),
};
export const mandateAPI = {
  liste: (params) => api.get('/mandate', { params }),
  statistik: () => api.get('/mandate/statistik'),
  lesen: (id) => api.get(`/mandate/${id}`),
  fuerZahler: (leadId) => api.get(`/mandate/zahler/${leadId}`),
  widerrufen: (id, grund) => api.post(`/mandate/${id}/widerrufen`, { grund }),
  zuordnen: (id, zahler_id) => api.post(`/mandate/${id}/zuordnen`, { zahler_id }),
  importSticky: (formData) => api.post('/mandate/import-sticky', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};
// Trainer-Chat (29.10.2026, backend/trainer_chat.py): Leitung ↔ Trainer-Link, Wochentermine, gelesen
export const trainerChatAPI = {
  uebersicht: () => api.get('/trainer-chat'),
  verlauf: (linkId) => api.get(`/trainer-chat/${linkId}`),
  vorschau: (linkId, text) => api.post(`/trainer-chat/${linkId}/vorschau`, { text }),
  senden: (linkId, daten) => api.post(`/trainer-chat/${linkId}`, daten),
  linkLesen: (token) => api.get(`/public/trainer/${token}/chat`),
  linkNeu: (token) => api.get(`/public/trainer/${token}/chat/neu`),
  linkAntworten: (token, text) => api.post(`/public/trainer/${token}/chat`, { text }),
};

export const trainerKalenderAPI = {
  lesen: (token, von, bis) => api.get(`/public/trainer/${token}`, { params: { von, bis } }),
  anlegen: (token, daten) => api.post(`/public/trainer/${token}/termine`, daten),
  aendern: (token, id, daten) => api.patch(`/public/trainer/${token}/termine/${id}`, daten),
  onboarding: (token) => api.post(`/public/trainer/${token}/onboarding`),   // 29.10.2026: Einführung abgeschlossen
  // 15.10.2026: PT-Anfrage aus dem CRM annehmen/ablehnen
  antwort: (token, id, daten) => api.post(`/public/trainer/${token}/termine/${id}/antwort`, daten),
  sprache: (token, { audio, text }) => {
    const fd = new FormData();
    if (audio) fd.append('audio', audio, audio.name || 'termin.webm');
    if (text) fd.append('text', text);
    return api.post(`/public/trainer/${token}/sprache`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  // Leads ueber den Link (28.09.2026): Interessenten eintragen, eigene Tipps sehen
  leads: (token) => api.get(`/public/trainer/${token}/leads`),
  leadAnlegen: (token, daten) => api.post(`/public/trainer/${token}/leads`, daten),
  // Anwesenheit ueber den Link (28.09.2026): gleiche Tafel wie /trainer im CRM, ohne Login
  anwesenheit: (token, datum) => api.get(`/public/trainer/${token}/anwesenheit`, { params: datum ? { datum } : {} }),
  anwesenheitStatus: (token, bid, status) => api.post(`/public/trainer/${token}/anwesenheit/buchung/${bid}/status`, { status }),
  anwesenheitHinzufuegen: (token, cid, mid) => api.post(`/public/trainer/${token}/anwesenheit/kurs/${cid}/hinzufuegen/${mid}`),
  anwesenheitSuche: (token, q) => api.get(`/public/trainer/${token}/anwesenheit/suche`, { params: { q } }),
  anwesenheitSprache: (token, cid, { audio, text }) => {
    const fd = new FormData();
    if (audio) fd.append('audio', audio, audio.name || 'anwesenheit.webm');
    if (text) fd.append('text', text);
    return api.post(`/public/trainer/${token}/anwesenheit/kurs/${cid}/sprache`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  anwesenheitMehrere: (token, cid, ids) => api.post(`/public/trainer/${token}/anwesenheit/kurs/${cid}/anwesend-mehrere`, { member_ids: ids }),
  anwesenheitNotiz: (token, mid, text) => api.post(`/public/trainer/${token}/anwesenheit/mitglied/${mid}/notiz`, { text }),
};
export const checkupTrainerAPI = {
  lesen: () => api.get('/checkup-trainer'),
  setzen: (lead_ids) => api.put('/checkup-trainer', { lead_ids }),
};
// Check-Up am Studio-Tablet (28.10.2026): nur Links mit Haken `checkup_tablet`; der gewählte Trainer reist bei jedem Aufruf mit
export const checkupTabletAPI = {
  trainer: (token) => api.get(`/public/trainer/${token}/checkup-trainer`),
  liste: (token, trainer) => api.get(`/public/trainer/${token}/checkups`, { params: { trainer } }),
  ablauf: (token, trainer, id) => api.get(`/public/trainer/${token}/checkup/${id}/ablauf`, { params: { trainer } }),
  einwilligung: (token, trainer, id, daten) => api.post(`/public/trainer/${token}/checkup/${id}/einwilligung`, daten, { params: { trainer } }),
  messung: (token, trainer, id, code) => api.post(`/public/trainer/${token}/checkup/${id}/messung`, { code }, { params: { trainer } }),
  antworten: (token, trainer, id, antworten) => api.put(`/public/trainer/${token}/checkup/${id}/antworten`, { antworten }, { params: { trainer } }),
  plan: (token, trainer, id) => api.post(`/public/trainer/${token}/checkup/${id}/plan`, {}, { params: { trainer }, timeout: 120000 }),
  planFreigeben: (token, trainer, id) => api.post(`/public/trainer/${token}/checkup/${id}/plan/freigeben`, {}, { params: { trainer } }),
  kurse: (token, trainer, id, termine) => api.post(`/public/trainer/${token}/checkup/${id}/kurse`, { termine }, { params: { trainer } }),
  pt: (token, trainer, id, daten) => api.post(`/public/trainer/${token}/checkup/${id}/pt`, daten, { params: { trainer } }),
  ernaehrung: (token, trainer, id, ja) => api.post(`/public/trainer/${token}/checkup/${id}/ernaehrung`, { ja }, { params: { trainer } }),
  abschliessen: (token, trainer, id) => api.post(`/public/trainer/${token}/checkup/${id}/abschliessen`, {}, { params: { trainer } }),
  // Kundenakte Schritt 3+4 (28.10.2026): Messwerte von Hand, Befund, Empfehlung, Abschluss mit vier Ausgängen
  messwerte: (token, trainer, id, daten) => api.put(`/public/trainer/${token}/checkup/${id}/messwerte`, daten, { params: { trainer } }),
  befund: (token, trainer, id, daten) => api.put(`/public/trainer/${token}/checkup/${id}/befund`, daten, { params: { trainer } }),
  empfehlung: (token, trainer, id) => api.post(`/public/trainer/${token}/checkup/${id}/empfehlung`, {}, { params: { trainer } }),
  abschluss: (token, trainer, id, daten) => api.post(`/public/trainer/${token}/checkup/${id}/abschluss`, daten, { params: { trainer }, timeout: 60000 }),
  art: (token, trainer, id, art) => api.put(`/public/trainer/${token}/checkup/${id}/art`, { art }, { params: { trainer } }),
  // w1-ergebnis: Ergebnis-/Abschluss-Check-up am Tablet
  ergebnis: (token, trainer, id) => api.get(`/public/trainer/${token}/checkup/${id}/ergebnis`, { params: { trainer } }),
  einschaetzungVorschlag: (token, trainer, id) => api.post(`/public/trainer/${token}/checkup/${id}/einschaetzung/vorschlag`, {}, { params: { trainer } }),
  einschaetzung: (token, trainer, id, daten) => api.put(`/public/trainer/${token}/checkup/${id}/einschaetzung`, daten, { params: { trainer } }),
  weg: (token, trainer, id, daten) => api.put(`/public/trainer/${token}/checkup/${id}/weg`, daten, { params: { trainer } }),
  foto: (token, trainer, id, fid) => api.get(`/public/trainer/${token}/checkup/${id}/ergebnis/foto/${fid}`, { params: { trainer }, responseType: 'blob' }),
};
// Übungsmodus am Studio-Tablet (28.10.2026): dieselben Aufrufe wie checkupTabletAPI, Stand nur in checkup_uebungen — nichts zählt
const uebungPfad = (token, id, rest) => `/public/trainer/${token}/uebung/${id}/${rest}`;
export const checkupUebungAPI = {
  liste: (token, trainer) => api.get(`/public/trainer/${token}/uebung`, { params: { trainer } }),
  zuruecksetzen: (token, trainer) => api.post(`/public/trainer/${token}/uebung/zuruecksetzen`, {}, { params: { trainer } }),
  ablauf: (token, trainer, id) => api.get(uebungPfad(token, id, 'ablauf'), { params: { trainer } }),
  einwilligung: (token, trainer, id, daten) => api.post(uebungPfad(token, id, 'einwilligung'), daten, { params: { trainer } }),
  messung: (token, trainer, id, code) => api.post(uebungPfad(token, id, 'messung'), { code }, { params: { trainer } }),
  antworten: (token, trainer, id, antworten) => api.put(uebungPfad(token, id, 'antworten'), { antworten }, { params: { trainer } }),
  kurse: (token, trainer, id, termine) => api.post(uebungPfad(token, id, 'kurse'), { termine }, { params: { trainer } }),
  pt: (token, trainer, id, daten) => api.post(uebungPfad(token, id, 'pt'), daten, { params: { trainer } }),
  ernaehrung: (token, trainer, id, ja) => api.post(uebungPfad(token, id, 'ernaehrung'), { ja }, { params: { trainer } }),
  abschliessen: (token, trainer, id) => api.post(uebungPfad(token, id, 'abschliessen'), {}, { params: { trainer } }),
  // neuer Ablauf des Steppers (Kundenakte): nur im Übungsdokument vermerkt
  messwerte: (token, trainer, id, daten) => api.put(uebungPfad(token, id, 'messwerte'), daten, { params: { trainer } }),
  befund: (token, trainer, id, daten) => api.put(uebungPfad(token, id, 'befund'), daten, { params: { trainer } }),
  empfehlung: (token, trainer, id) => api.post(uebungPfad(token, id, 'empfehlung'), {}, { params: { trainer } }),
  abschluss: (token, trainer, id, daten) => api.post(uebungPfad(token, id, 'abschluss'), daten, { params: { trainer } }),
};
// Teilnahmehäufigkeit (v9 A, 23.10.2026): nur gelesen
export const teilnahmeAPI = {
  liste: (params) => api.get('/teilnahme', { params }),
  csv: (params) => api.get('/teilnahme/export.csv', { params, responseType: 'blob' }),
  mitglied: (leadId) => api.get(`/teilnahme/mitglied/${leadId}`),
};

// Adressen nachtragen (23.10.2026): Lead + laufende Verträge, Liste „fehlt“
export const adressenAPI = {
  fehlend: () => api.get('/adressen/fehlend'),
  lesen: (leadId) => api.get(`/adressen/${leadId}`),
  setzen: (leadId, daten) => api.put(`/adressen/${leadId}`, daten),
};

// Gemeinsames Buchungsfenster (30.10.2026, backend/buchungsfenster)
export const buchungsfensterAPI = {
  trainer: () => api.get('/buchungsfenster/trainer'),
  personen: () => api.get('/buchungsfenster/personen'),
  luecken: (params) => api.get('/buchungsfenster/luecken', { params }),
  fruehester: (daten) => api.post('/buchungsfenster/fruehester', daten),
  pruefen: (daten) => api.post('/buchungsfenster/pruefen', daten),
  buchen: (daten) => api.post('/buchungsfenster/buchen', daten),
  gespraeche: (params) => api.get('/buchungsfenster/gespraeche', { params }),
  gespraechAbsagen: (id, daten) => api.post(`/buchungsfenster/gespraeche/${id}/absagen`, daten),
};

export const trainerLinksAPI = {
  liste: () => api.get('/trainer-links'),
  anlegen: (daten) => api.post('/trainer-links', daten),
  aendern: (id, daten) => api.patch(`/trainer-links/${id}`, daten),
  erneuern: (id) => api.post(`/trainer-links/${id}/neu`),
  loeschen: (id) => api.delete(`/trainer-links/${id}`),
  termine: (params) => api.get('/pt-termine', { params }),
  terminAnlegen: (daten) => api.post('/pt-termine', daten),
  abwesenheitAnlegen: (id, daten) => api.post(`/trainer-links/${id}/abwesenheiten`, daten),   // 29.10.2026
  abwesenheitLoeschen: (id, aid) => api.delete(`/trainer-links/${id}/abwesenheiten/${aid}`),
  ptTrainer: () => api.get('/pt-trainer'),
  terminAendern: (id, daten) => api.patch(`/pt-termine/${id}`, daten),
  terminLoeschen: (id) => api.delete(`/pt-termine/${id}`),
};
export const stufenAPI = {
  nachtjobReport: () => api.get('/stufen/nachtjob'),
};
